// D:\Casticart\frontend\src\components\admin\events\EventDetails.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../components/admin/Sidebar';
import ArtistBubbleView from '../../components/admin/artists/ArtistBubbleView';
import { MemberProfileModal as AdminMemberProfileModal } from '../../components/admin/chats/MemberListModal';
import EventListRow from '../../components/common/events/EventListRow';
import EventMaterialExplorer from '../../components/common/events/EventMaterialExplorer';
import EventImageGallery from '../../components/common/events/EventImageGallery';
import EventFlyerMedia from '../../components/common/EventFlyerMedia';
import EventVenueDisplay, { buildVenueAddress, buildVenueMapUrl } from '../../components/common/events/EventVenueDisplay';
import ExplanationButton from '../../components/common/ExplanationButton';
import PersonAvatar from '../../components/common/avatar/PersonAvatar';
import '../../css/admin/events/EventDetail.css';
import '../../css/common/events/EventListCard.css';
import '../../css/common/events/EventListRow.css';
import { formatDateTimeJST } from '../../utils/datetime';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { useToast } from '../../components/common/ToastSystem';
import DetailPageSkeleton from '../../components/common/DetailPageSkeleton';
import { useAuth } from '../../contexts/AuthContext';
import { getSocket } from '../../lib/socket';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import { normalizeGenresPayload } from '../../utils/genreUtils';
import { resolveEventDetailPermission } from '../../constants/eventDetailPermissions';
import { resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import { openExternalUrl } from '../../utils/externalLink';
import useEventMembersVisibilityControl from '../../components/common/events/useEventMembersVisibilityControl';
import EventMembersVisibilityConfirmModal from '../../components/common/events/EventMembersVisibilityConfirmModal';
import EventDetailsPageLayout from '../../components/admin/events/EventDetailsPageLayout';
import {
  fetchEventAssignmentAdminCandidates,
  updateEventAssignments,
} from '../../utils/eventAssignments';
import { useConfirm } from '../../design-system/ConfirmDialog';
import {
  getEventDetail as fetchEventDetailFromApi,
  getEventTicketSalesSummary as fetchEventTicketSalesSummaryFromApi,
  getEventTimetableRuntimeState,
  listEventTimetables as listEventTimetablesFromApi,
} from '../../api/taskEvents';
import { getAdminSession } from '../../api/auth';
import { getVenueDetail } from '../../api/venues';
import {
  cancelAdminEvent,
  deleteAdminEventBanner,
  deleteAdminEventFlyer,
  deleteEventPerformanceFlyer,
  getAdminPlBookSummary,
  getAdminEventCastingProgress,
  getAdminStageSeatingSharedSummary,
  getEventNotificationSilence,
  addEventManualParticipant,
  getEventChatGroupSetting,
  getEventGroupEvents,
  listAdminEventPlBooks,
  listEventCastingInfo,
  previewEventChatGroupSetting,
  removeEventManualParticipant,
  uploadAdminEventBanner,
  uploadAdminEventFlyer,
  uploadEventPerformanceFlyer,
  updateEventChatGroupSetting,
  updateEventNotificationSilence,
} from '../../api/eventDetails';
import { listAdminArtists } from '../../api/artists';
import {
  bulkSetCancelCompensation,
  listCancelCompensationTargets,
  listEventContractArtistStatuses,
} from '../../api/artistCastings';
import {
  createTaskComment,
  createTaskDependency,
  createTaskItem,
  deleteTaskAttachment,
  deleteTaskDependency,
  deleteTaskItem,
  downloadTaskAttachment,
  ensurePrimaryEventTaskProject,
  getPrimaryEventTaskProject,
  getTaskProjectDetail,
  listTaskAttachments,
  listTaskComments,
  listTaskDependencies,
  toggleTaskCommentReaction,
  updateTaskItem,
  uploadTaskAttachment,
} from '../../api/taskProjectDetail';
import { listGenres } from '../../api/genres';
import {
  API_URL,
  FLYER_IMAGE_PATH,
  BANNER_IMAGE_PATH,
  ARTIST_PROFILE_IMAGE_PATH,
  PARTICIPANT_STATUSES,
  PERFORMANCE_TYPE_JAPANESE,
  TASK_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_ORDER,
  normalizeSelectedOptions,
  EVENT_TASK_FOCUS_LIMIT,
  TASK_STATUS_OPTIONS,
  TASK_COMMENT_REACTION_OPTIONS,
  TASK_COMMENT_REACTION_EMOJI_SET,
  mapEventTaskCommentReactions,
  normalizeTaskPriority,
  isTaskDoneStatus,
  normalizeEventTaskStatus,
  toDateKey,
  calcDueDays,
  getDueTone,
  formatDueHint,
  getEventTaskPrimaryAssignee,
  getEventTaskAssigneeLabel,
  getEventTaskAssigneeAvatarUrl,
  getEventTaskCommentAuthorName,
  getEventTaskCommentAuthorAvatarUrl,
  appendVariant,
  toTimestamp,
  formatYenText,
  formatFileSize,
  buildParticipantProfile,
  normalizeGenderBucket,
  calcAgeFromParts,
  parsePgIntArray,
  getDefaultColumnAlign,
  DEFAULT_TITLE_STYLE,
  DEFAULT_TIMETABLE_COLUMNS,
  buildAddress,
  UNSUPPORTED_IMAGE_MESSAGE,
  CHAT_GROUP_MODE,
  CHAT_GROUP_EXECUTE_MODE,
  CHAT_GROUP_MODE_LABEL,
  isSupportedImageFile,
} from '../../components/admin/events/eventDetailsHelpers';
import { labelStatus, TicketLineChart, renderGenreBadges } from '../../components/admin/events/eventDetailsParts';
import useEventDetailsTaskModel from './useEventDetailsElement.taskModel';
import useEventDetailsTaskActions from './useEventDetailsElement.taskActions';
import useEventDetailsRuntimePreview, {
  buildAdjustmentMap,
  buildMemoMap,
} from './useEventDetailsElement.runtimePreview';
import useEventDetailsAssets from './useEventDetailsElement.assets';
import useEventDetailsGroupSales from './useEventDetailsElement.groupSales';
import useEventDetailsView from './useEventDetailsElement.view';
import useEventDetailsPermissions from './useEventDetailsElement.permissions';
import useEventCastingSummary from './useEventCastingSummary';

export default function useEventDetailsElement() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { error: toastError, success: toastSuccess } = useToast();
  const { user: authUser } = useAuth();

  // ── Hooks（順序固定/先頭に集約）────────────────────────────────────────
  const [eventData, setEventData] = useState(null);
  const [showCompactEventHeader, setShowCompactEventHeader] = useState(false);
  const confirmDialog = useConfirm();
  const [flyerUrls, setFlyerUrls] = useState({ front: null, back: null });
  const [currentFlyerSide, setCurrentFlyerSide] = useState('front');
  const [showFlyerPopup, setShowFlyerPopup] = useState(false);
  const [genres, setGenres] = useState([]);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [venueLink, setVenueLink] = useState(null);
  const [venueResolvedAddress, setVenueResolvedAddress] = useState('');
  const [venueResolvedName, setVenueResolvedName] = useState('');
  const [parentEventInfo, setParentEventInfo] = useState(null);
  const [groupEvents, setGroupEvents] = useState([]);
  const [hasGroupEvents, setHasGroupEvents] = useState(false);
  const [groupEventsLoading, setGroupEventsLoading] = useState(false);
  const [groupRootEventUuid, setGroupRootEventUuid] = useState(null);
  const [chatGroupSetting, setChatGroupSetting] = useState({
    has_group: false,
    group_root_event_uuid: null,
    group_event_count: 0,
    chat_mode: CHAT_GROUP_MODE.SEPARATE,
    unified_thread_id: null,
    updated_by_admin_id: null,
    updated_at: null,
  });
  const [chatGroupSettingLoading, setChatGroupSettingLoading] = useState(false);
  const [chatGroupDraftMode, setChatGroupDraftMode] = useState(CHAT_GROUP_MODE.SEPARATE);
  const [chatGroupPreviewLoading, setChatGroupPreviewLoading] = useState(false);
  const [chatGroupSaving, setChatGroupSaving] = useState(false);
  const [chatGroupConfirmState, setChatGroupConfirmState] = useState({
    open: false,
    executeMode: null,
    targetMode: CHAT_GROUP_MODE.SEPARATE,
    preview: null,
  });
  const [groupFlyerLandscapeMap, setGroupFlyerLandscapeMap] = useState({});
  const [flyerActionLoading, setFlyerActionLoading] = useState({ front: false, back: false });
  const [performanceFlyerLoading, setPerformanceFlyerLoading] = useState(false);
  const [selectedMultiPerformanceId, setSelectedMultiPerformanceId] = useState(null);
  const [bannerActionLoading, setBannerActionLoading] = useState(false);
  const flyerFileInputsRef = useRef({ front: null, back: null });
  const performanceFlyerInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);
  const [inheritedAssetSource, setInheritedAssetSource] = useState(null);
  const [notificationSilenceWindows, setNotificationSilenceWindows] = useState([]);
  const [notificationSilenceDrafts, setNotificationSilenceDrafts] = useState([]);
  const [notificationSilenceModalOpen, setNotificationSilenceModalOpen] = useState(false);
  const [notificationSilenceLoading, setNotificationSilenceLoading] = useState(false);
  const [notificationSilenceSaving, setNotificationSilenceSaving] = useState(false);

  const [castingInfo, setCastingInfo] = useState([]);
  const [assignmentRows, setAssignmentRows] = useState([]);   // このイベントの割当 + 基本プロフィール
  const [participantView, setParticipantView] = useState('list');
  const [participantProfile, setParticipantProfile] = useState(null);
  const [manualParticipantModalOpen, setManualParticipantModalOpen] = useState(false);
  const [manualParticipantSearch, setManualParticipantSearch] = useState('');
  const [manualParticipantCandidates, setManualParticipantCandidates] = useState([]);
  const [manualParticipantLoading, setManualParticipantLoading] = useState(false);
  const [manualParticipantSavingId, setManualParticipantSavingId] = useState('');
  const [manualParticipantRemovingId, setManualParticipantRemovingId] = useState('');
  // マルチ契約横断の最新ステータス + 契約識別子
  const [latestByArtist, setLatestByArtist] = useState(new Map());
  const [contractStatusRows, setContractStatusRows] = useState([]);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentSettingsOpen, setAssignmentSettingsOpen] = useState(false);
  const [assignmentCandidates, setAssignmentCandidates] = useState([]);
  const [assignmentCandidatesLoading, setAssignmentCandidatesLoading] = useState(false);
  const [assignmentDraftIds, setAssignmentDraftIds] = useState([]);
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [showCancelCompModal, setShowCancelCompModal] = useState(false);
  const [cancelCompMode, setCancelCompMode] = useState('percent'); // 'percent' | 'fixed'
  const [cancelCompPercent, setCancelCompPercent] = useState('');
  const [cancelCompFixed, setCancelCompFixed] = useState('');
  const [cancelCompTargets, setCancelCompTargets] = useState([]);
  const [cancelCompLoading, setCancelCompLoading] = useState(false);
  const [cancelCompSaving, setCancelCompSaving] = useState(false);
  const [cancelCompDebouncedMode, setCancelCompDebouncedMode] = useState('percent');
  const [cancelCompDebouncedValue, setCancelCompDebouncedValue] = useState(null);
  const [cancelCompCalcBusy, setCancelCompCalcBusy] = useState(false);
  const [sessionRoleOverride, setSessionRoleOverride] = useState(null);
  const [sessionPermissionsOverride, setSessionPermissionsOverride] = useState(null);
  const [sessionPermissionsHydrated, setSessionPermissionsHydrated] = useState(false);
  const cancelCompCalcTimerRef = useRef(null);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [ticketSummaryLoading, setTicketSummaryLoading] = useState(false);
  const [eventFinanceSummary, setEventFinanceSummary] = useState(null);
  const [eventFinanceSummaryLoading, setEventFinanceSummaryLoading] = useState(false);
  const [timetables, setTimetables] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [activeTimetableTab, setActiveTimetableTab] = useState(null);
  const [stageSeatingSummary, setStageSeatingSummary] = useState({ layouts: [], shares: [] });
  const [stageSeatingSummaryLoading, setStageSeatingSummaryLoading] = useState(false);
  const [eventTaskProject, setEventTaskProject] = useState(null);
  const [eventTaskRecentTasks, setEventTaskRecentTasks] = useState([]);
  const [eventTaskAllTasks, setEventTaskAllTasks] = useState([]);
  const [eventTaskSections, setEventTaskSections] = useState([]);
  const [eventTaskAssignableAdmins, setEventTaskAssignableAdmins] = useState([]);
  const [eventTaskCanEdit, setEventTaskCanEdit] = useState(false);
  const [eventTaskLoading, setEventTaskLoading] = useState(false);
  const [eventTaskAllTasksLoading, setEventTaskAllTasksLoading] = useState(false);
  const [eventTaskCreating, setEventTaskCreating] = useState(false);
  const [eventTaskUpdatingId, setEventTaskUpdatingId] = useState('');
  const [eventTaskDetailModal, setEventTaskDetailModal] = useState(null);
  const [eventTaskDetailFeedTab, setEventTaskDetailFeedTab] = useState('comments');
  const [eventTaskMetaEditField, setEventTaskMetaEditField] = useState('');
  const [eventTaskDetailForm, setEventTaskDetailForm] = useState({
    assigneeId: '',
    startsOn: '',
    dueOn: '',
    status: 'todo',
    priority: 'normal',
    description: '',
  });
  const [eventTaskDetailSaving, setEventTaskDetailSaving] = useState(false);
  const [eventTaskDetailDeleting, setEventTaskDetailDeleting] = useState(false);
  const [eventTaskDetailComments, setEventTaskDetailComments] = useState([]);
  const [eventTaskDetailCommentsLoading, setEventTaskDetailCommentsLoading] = useState(false);
  const [eventTaskDetailCommentBody, setEventTaskDetailCommentBody] = useState('');
  const [eventTaskDetailPostingComment, setEventTaskDetailPostingComment] = useState(false);
  const [eventTaskCommentReactionBusyMap, setEventTaskCommentReactionBusyMap] = useState({});
  const [eventTaskNewSubtaskTitle, setEventTaskNewSubtaskTitle] = useState('');
  const [eventTaskNewSubtaskDueOn, setEventTaskNewSubtaskDueOn] = useState('');
  const [eventTaskNewSubtaskAssigneeId, setEventTaskNewSubtaskAssigneeId] = useState('');
  const [eventTaskNewSubtaskInlineEditField, setEventTaskNewSubtaskInlineEditField] = useState('');
  const [eventSubtaskInlineEdit, setEventSubtaskInlineEdit] = useState({ taskId: '', field: '' });
  const [eventTaskCreatingSubtask, setEventTaskCreatingSubtask] = useState(false);
  const [eventTaskDependencies, setEventTaskDependencies] = useState({
    predecessors: [],
    successors: [],
  });
  const [eventTaskDependenciesLoading, setEventTaskDependenciesLoading] = useState(false);
  const [eventTaskDependencyLinkType, setEventTaskDependencyLinkType] = useState('predecessor');
  const [eventTaskDependencyQuery, setEventTaskDependencyQuery] = useState('');
  const [eventTaskDependencyPickerFocused, setEventTaskDependencyPickerFocused] = useState(false);
  const [eventTaskAddingDependency, setEventTaskAddingDependency] = useState(false);
  const [eventTaskAddingDependencyTaskId, setEventTaskAddingDependencyTaskId] = useState('');
  const [eventTaskRemovingDependencyTaskId, setEventTaskRemovingDependencyTaskId] = useState('');
  const [eventTaskAttachments, setEventTaskAttachments] = useState([]);
  const [eventTaskAttachmentsLoading, setEventTaskAttachmentsLoading] = useState(false);
  const [eventTaskUploadingAttachment, setEventTaskUploadingAttachment] = useState(false);
  const [eventTaskDeletingAttachmentId, setEventTaskDeletingAttachmentId] = useState('');
  const [eventTaskError, setEventTaskError] = useState('');
  const [hoveredPreviewRowId, setHoveredPreviewRowId] = useState(null);
  const [pinnedPreviewRowIds, setPinnedPreviewRowIds] = useState(new Set());
  const [nowTimetableRowId, setNowTimetableRowId] = useState(null);
  const [runtimeData, setRuntimeData] = useState({ adjustments: [], memos: [], announcements: [] });
  const highlightTimerRef = useRef(null);
  const timetableScrolledRef = useRef(false);
  const eventTaskDependencyBlockRef = useRef(null);
  const eventTaskAttachmentBlockRef = useRef(null);
  const eventTaskAttachmentInputRef = useRef(null);

  const flyerImages = useMemo(
    () => [flyerUrls.front, flyerUrls.back].filter(Boolean),
    [flyerUrls.front, flyerUrls.back],
  );
  const adjustmentsMap = useMemo(
    () => buildAdjustmentMap(runtimeData?.adjustments || []),
    [runtimeData?.adjustments],
  );
  const memoMap = useMemo(
    () => buildMemoMap(runtimeData?.memos || []),
    [runtimeData?.memos],
  );
  const loggedInAdminId = useMemo(() => {
    if (!authUser) return null;
    return authUser.userId ?? authUser.user?.userId ?? authUser.id ?? null;
  }, [authUser]);
  useEffect(() => {
    setShowCompactEventHeader(false);
  }, [eventId]);
  useEffect(() => {
    let active = true;
    setSessionPermissionsHydrated(false);
    const refreshAdminSession = async () => {
      try {
        const data = await getAdminSession();
        if (!active || !data?.isLoggedIn) return;
        const role = String(data.role || data.adminRole || '').trim().toLowerCase();
        const permissions = Array.isArray(data.permissions)
          ? data.permissions
          : data.permissions
            ? [data.permissions]
            : [];
        setSessionRoleOverride(role || null);
        setSessionPermissionsOverride(permissions);
      } catch (_) {
      } finally {
        if (active) {
          setSessionPermissionsHydrated(true);
        }
      }
    };
    refreshAdminSession();
    return () => {
      active = false;
    };
  }, [eventId]);
  const eventDetailsPermissions = useEventDetailsPermissions({
    authUser,
    resolveAdminPermission,
    resolveEventDetailPermission,
    sessionPermissionsHydrated,
    sessionPermissionsOverride,
    sessionRoleOverride,
  });
  const {
    isTaskOnlyRole,
    canViewCastingSettings,
    canViewParticipants,
    canViewTicketSales,
    canViewEventTasks,
    canViewTimetable,
    canViewContractStatus,
    canUseEventChat,
    canEditEventInfo,
    canEditCastingSettings,
    canEditParticipants,
    canEditTicketSales,
    canEditEventTasks,
    eventReadOnlyTitle,
  } = eventDetailsPermissions;
  const canViewCastingProgress =
    canViewCastingSettings || canViewParticipants || canViewContractStatus;
  const {
    castingProgressPercent,
    castingProgressNote,
    refreshCastingSummary,
  } = useEventCastingSummary({ eventId, enabled: canViewCastingProgress });
  const hasRuntimeAccess = useMemo(() => {
    if (!loggedInAdminId) return false;
    const list = Array.isArray(eventData?.assigned_admins) ? eventData.assigned_admins : [];
    return list.some((admin) => {
      const adminId = admin?.admin_user_id ?? admin?.id ?? null;
      if (adminId === null || adminId === undefined) return false;
      return Number(adminId) === Number(loggedInAdminId);
    });
  }, [eventData, loggedInAdminId]);

  const fetchTimetableRuntime = useCallback(
    async (tableId) => {
      if (!eventId || !tableId || !canViewTimetable) {
        setRuntimeData({ adjustments: [], memos: [], announcements: [] });
        return;
      }
      if (!hasRuntimeAccess) {
        setRuntimeData({ adjustments: [], memos: [], announcements: [] });
        return;
      }
      try {
        const json = await getEventTimetableRuntimeState(eventId, tableId);
        setRuntimeData({
          adjustments: Array.isArray(json?.adjustments) ? json.adjustments : [],
          memos: Array.isArray(json?.memos) ? json.memos : [],
          announcements: Array.isArray(json?.announcements) ? json.announcements : [],
        });
      } catch (err) {
        if (err?.status === 403) {
          setRuntimeData({ adjustments: [], memos: [], announcements: [] });
          return;
        }
        console.error('Error fetching timetable runtime:', err);
        setRuntimeData({ adjustments: [], memos: [], announcements: [] });
        toastError(err.message || 'タイムテーブルの実行情報の取得に失敗しました');
      }
    },
    [canViewTimetable, eventId, hasRuntimeAccess, toastError],
  );
  const hasMultipleFlyers = flyerImages.length > 1;
  const currentFlyerUrl = flyerUrls[currentFlyerSide] || null;
  const flyerToggleLabel = currentFlyerSide === 'front' ? '裏面へ切替' : '表面へ切替';
  const toggleFlyerFace = useCallback(() => {
    setCurrentFlyerSide((prev) => (prev === 'front' ? 'back' : 'front'));
  }, []);

  const { getEventUnreadCount, refreshEventChatUnread, markEventAsRead } = useMessageCount();
  const unread = getEventUnreadCount(eventId) || 0;
  const isEventCancelled = !!eventData?.canceled_at;
  const canViewPublicStatus = eventData?.public_status_visible !== false;
  const canViewPublishSchedule = eventData?.publish_schedule_visible !== false;
  const isInfoExplicitlyUnpublished =
    canViewPublicStatus && eventData?.info_is_published === false;
  const infoPublishAtLabel = useMemo(
    () =>
      canViewPublishSchedule && eventData?.info_publish_at
        ? formatDateTimeJST(eventData.info_publish_at)
        : '',
    [canViewPublishSchedule, eventData?.info_publish_at]
  );
  const isConciergeEvent = Boolean(eventData?.is_concierge_event || eventData?.concierge_application);
  const conciergeApplication = eventData?.concierge_application || null;
  const parentEventLabel = useMemo(() => {
    if (!eventData?.original_event_uuid) return '';
    const fromState = parentEventInfo
      ? [parentEventInfo.name, parentEventInfo.performance].filter(Boolean).join(' ')
      : '';
    const fromInherited = inheritedAssetSource
      ? [inheritedAssetSource.name, inheritedAssetSource.performance].filter(Boolean).join(' ')
      : '';
    const name =
      eventData.original_event_name ||
      eventData.original_event_title ||
      '';
    const perf =
      eventData.original_event_performance ||
      eventData.original_performance_title ||
      eventData.original_performance_name ||
      '';
    return fromState || fromInherited || [name, perf].filter(Boolean).join(' ');
  }, [
    parentEventInfo,
    inheritedAssetSource,
    eventData?.original_event_uuid,
    eventData?.original_event_name,
    eventData?.original_event_title,
    eventData?.original_event_performance,
    eventData?.original_performance_title,
    eventData?.original_performance_name,
  ]);
  const parentEventLink = useMemo(
    () => (eventData?.original_event_uuid ? `/admin/workspaces/event-operations/events/${eventData.original_event_uuid}` : null),
    [eventData?.original_event_uuid],
  );

  const venueId = useMemo(() => {
    const raw = eventData?.venue_id ?? eventData?.venue?.id ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    return String(raw);
  }, [eventData?.venue_id, eventData?.venue]);

  const venueDisplayName = useMemo(() => {
    if (venueId) return (venueResolvedName || eventData?.venue || '').trim();
    return (eventData?.venue || '').trim();
  }, [venueId, venueResolvedName, eventData?.venue]);

  const mapLinkUrl = useMemo(() => {
    return buildVenueMapUrl({
      name: venueDisplayName,
      address: venueResolvedAddress,
      fallback: eventData?.prefecture || '',
    });
  }, [venueDisplayName, venueResolvedAddress, eventData?.prefecture]);

  // fetch 群
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      const jobs = [fetchEventDetails(), fetchGroupEvents(), fetchChatGroupSetting(), fetchGenres()];
      if (canViewCastingSettings || canViewParticipants || canViewContractStatus) {
        jobs.push(fetchCastingInfo());
      } else {
        setCastingInfo([]);
      }
      if (canViewParticipants || canViewContractStatus) {
        jobs.push(fetchAssignmentsForThisEvent(), fetchLatestStatusAcrossContracts());
      } else {
        setAssignmentRows([]);
        setLatestByArtist(new Map());
        setContractStatusRows([]);
      }
      if (canEditEventInfo) {
        jobs.push(fetchNotificationSilence());
      } else {
        setNotificationSilenceWindows([]);
        setNotificationSilenceDrafts([]);
      }
      if (canViewTicketSales) {
        jobs.push(fetchTicketSalesSummary());
        jobs.push(fetchEventFinanceSummary());
      } else {
        setTicketSummary(null);
        setEventFinanceSummary(null);
      }
      if (canViewTimetable) {
        jobs.push(fetchTimetables());
      } else {
        setTimetables([]);
        setActiveTimetableTab(null);
      }
      if (canViewParticipants) {
        jobs.push(fetchStageSeatingSummary());
      } else {
        setStageSeatingSummary({ layouts: [], shares: [] });
      }
      if (canViewEventTasks) {
        jobs.push(fetchEventTaskProject());
      } else {
        setEventTaskProject(null);
        setEventTaskRecentTasks([]);
        setEventTaskAllTasks([]);
        setEventTaskCanEdit(false);
      }
      await Promise.all(jobs);
      refreshEventChatUnread();
    })().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventId,
    canViewCastingSettings,
    canViewParticipants,
    canViewContractStatus,
    canViewTicketSales,
    canViewTimetable,
    canViewEventTasks,
  ]);

  useEffect(() => {
    setPinnedPreviewRowIds(new Set());
    setHoveredPreviewRowId(null);
  }, [activeTimetableTab, timetables]);

  useEffect(() => {
    if (canViewParticipants) return;
    setParticipantProfile(null);
  }, [canViewParticipants]);

  useEffect(() => {
    if (!canViewTimetable || !activeTimetableTab) {
      setRuntimeData({ adjustments: [], memos: [], announcements: [] });
      return;
    }
    fetchTimetableRuntime(activeTimetableTab);
  }, [activeTimetableTab, canViewTimetable, fetchTimetableRuntime]);

  useEffect(() => {
    if (!eventId) return;
    if (!canViewTimetable) return;
    const socket = getSocket();
    if (!socket) return;
    if (!socket.connected) {
      try {
        socket.connect();
      } catch (err) {
        // ignore connect errors
      }
    }
    try {
      socket.emit(SOCKET_EVENTS.JOIN_EVENT, eventId);
    } catch (err) {
      // ignore join errors
    }
    const eventName = SOCKET_EVENTS.TIMETABLE_RUNTIME_UPDATE || 'timetable_runtime_update';
    const handleRuntimeUpdate = (payload = {}) => {
      if (payload?.eventId && String(payload.eventId) !== String(eventId)) return;
      const updatedTimetableId = payload?.timetableId ?? payload?.timetable_id ?? null;
      if (updatedTimetableId && activeTimetableTab && Number(updatedTimetableId) !== Number(activeTimetableTab)) {
        return;
      }
      if (!activeTimetableTab) return;
      fetchTimetableRuntime(activeTimetableTab);
    };
    socket.on(eventName, handleRuntimeUpdate);
    return () => {
      socket.off(eventName, handleRuntimeUpdate);
      try {
        socket.emit('leave_event', eventId);
      } catch (err) {
        // ignore leave errors
      }
    };
  }, [eventId, activeTimetableTab, canViewTimetable, fetchTimetableRuntime]);

  const clearHighlightTimer = useCallback(() => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
  }, []);

  // タイムテーブルタブの初期選択（リンク遷移で日付指定が来た場合 + それ以外は先頭）
  useEffect(() => {
    if (!timetables.length) return;
    const targetDate = location.state?.timetableDate;
    if (targetDate) {
      const normalized = String(targetDate).slice(0, 10);
      const found = timetables.find((t) => String(t.target_date || '').slice(0, 10) === normalized);
      if (found) {
        setActiveTimetableTab(found.id);
        return;
      }
    }
    if (!activeTimetableTab && timetables[0]) {
      setActiveTimetableTab(timetables[0].id);
    }
  }, [location.state?.timetableDate, timetables, activeTimetableTab]);

  // タイムテーブルセクションへスクロール（リンク遷移時）
  useEffect(() => {
    const wantsScroll =
      location.hash === '#timetable' || location.state?.scrollTo === 'timetable';
    if (!wantsScroll || !timetables.length) return;
    if (timetableScrolledRef.current) return;

    const tryScroll = (attempt = 0) => {
      const target =
        document.getElementById('timetable-section') ||
        document.querySelector('[data-section="timetable"]') ||
        document.querySelector('.timetable-section') ||
        document.querySelector('.timetable-table') ||
        document.querySelector('#timetable') ||
        document.querySelector('a[name="timetable"]');
      if (target) {
        timetableScrolledRef.current = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempt < 10) {
        setTimeout(() => tryScroll(attempt + 1), 250);
      }
    };

    tryScroll();
  }, [location.hash, location.state?.scrollTo, timetables]);

  const openTimetableViewer = useCallback(
    (tableId) => {
      if (!eventId || !tableId) return;
      const url = `/admin/workspaces/event-operations/events/${encodeURIComponent(eventId)}/timetables/${encodeURIComponent(tableId)}`;
      openExternalUrl(url);
    },
    [eventId],
  );

  // ── ユーティリティ（フックではない）───────────────────────────────────
  const toUrl = useCallback((base, filename) => `${API_URL}${base}/${encodeURIComponent(filename)}`, []);
  const formatDate = useCallback((v) => (v ? new Date(v).toLocaleDateString('ja-JP') : '未設定'), []);
  const formatTime = (t) => (t ? String(t).slice(0, 5) : '未設定');
  const formatCurrency = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('ja-JP');
  };
  const toDateTimeLocalValue = useCallback((v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 16);
    const parts = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const part = (type, fallback) => parts.find((p) => p.type === type)?.value || fallback;
    return `${part('year', '0000')}-${part('month', '01')}-${part('day', '01')}T${part('hour', '00')}:${part('minute', '00')}`;
  }, []);
  const dateTimeLocalToIso = useCallback((value) => {
    const text = String(value || '').trim();
    if (!text) return null;
    const date = new Date(`${text}:00+09:00`);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }, []);
  const createNotificationSilenceDraft = useCallback(() => ({
    enabled: true,
    label: '',
    silentStartsAt: '',
    silentEndsAt: '',
    soundCheckEnabled: false,
    soundCheckScheduledAt: '',
  }), []);
  const normalizeNotificationSilenceDraft = useCallback((windowItem = {}) => ({
    id: windowItem.id || null,
    enabled: windowItem.enabled !== false,
    label: windowItem.label || '',
    silentStartsAt: toDateTimeLocalValue(windowItem.silentStartsAt || windowItem.silent_starts_at),
    silentEndsAt: toDateTimeLocalValue(windowItem.silentEndsAt || windowItem.silent_ends_at),
    soundCheckEnabled: windowItem.soundCheckEnabled === true || windowItem.sound_check_enabled === true,
    soundCheckScheduledAt: toDateTimeLocalValue(
      windowItem.soundCheckScheduledAt || windowItem.sound_check_scheduled_at
    ),
  }), [toDateTimeLocalValue]);
  const {
    applyEventTaskToState,
    removeEventTaskFromState,
    formatTaskDateInput,
    jstTodayYmd,
    isToday,
    isTodayDate,
    eventTaskInsights,
    eventTaskDetailTask,
    eventTaskDetailSubtasks,
    eventTaskSectionNameMap,
    eventTaskDetailSectionName,
    eventTaskDetailDueMeta,
    eventTaskDetailParentTask,
    eventTaskDetailAssigneeLabel,
    eventTaskDetailAssigneeAvatarUrl,
    eventTaskNewSubtaskAssigneeLabel,
    eventTaskDependencyCandidateTasks,
  } = useEventDetailsTaskModel({
    EVENT_TASK_FOCUS_LIMIT,
    TASK_PRIORITY_ORDER,
    TASK_STATUS_LABELS,
    calcDueDays,
    eventData,
    eventTaskAllTasks,
    eventTaskAssignableAdmins,
    eventTaskDependencies,
    eventTaskDependencyLinkType,
    eventTaskDependencyQuery,
    eventTaskDetailModal,
    eventTaskNewSubtaskAssigneeId,
    eventTaskProject,
    eventTaskRecentTasks,
    eventTaskSections,
    formatDate,
    formatDueHint,
    getDueTone,
    getEventTaskAssigneeAvatarUrl,
    getEventTaskAssigneeLabel,
    getEventTaskPrimaryAssignee,
    isTaskDoneStatus,
    normalizeEventTaskStatus,
    normalizeTaskPriority,
    setEventTaskAllTasks,
    setEventTaskRecentTasks,
    toTimestamp,
  });

  const canCancelEvent = useMemo(() => {
    if (!canEditEventInfo || !authUser) return false;
    const perms = authUser.permissions;
    const hasAll =
      authUser.role === 'admin' ||
      perms === '*' ||
      perms === 'admin' ||
      perms?.includes?.('*') ||
      perms?.includes?.('admin');
    const hasCancel = perms?.includes?.('event_cancel');
    return hasAll || hasCancel;
  }, [authUser, canEditEventInfo]);

  const getParticipantImageStyle = (artist) => {
    if (artist.profile_picture) {
      const imageUrl = appendVariant(
        `${API_URL}${ARTIST_PROFILE_IMAGE_PATH}/${encodeURIComponent(artist.profile_picture)}`,
        'proxy'
      );
      return {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    const genderKey = String(artist.gender || artist.artist_gender || '').trim().toLowerCase();
    const bg =
      genderKey === 'male' || genderKey === 'man' || genderKey === 'm' || genderKey === '男性' || genderKey === '男'
        ? 'var(--person-avatar-artist-male-bg)'
        : genderKey === 'female' || genderKey === 'woman' || genderKey === 'f' || genderKey === '女性' || genderKey === '女'
        ? 'var(--person-avatar-artist-female-bg)'
        : 'var(--person-avatar-artist-unknown-bg)';
    return {
      backgroundColor: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: 600,
      color: '#fff',
    };
  };

  const openCancelModal = () => {
    setCancelReason('');
    setShowCancelModal(true);
    setShowCancelConfirm(false);
  };
  const closeCancelModal = () => {
    setShowCancelModal(false);
    setShowCancelConfirm(false);
  };
  const proceedCancelPreview = () => {
    setShowCancelModal(false);
    setShowCancelConfirm(true);
  };
  const submitCancel = async () => {
    if (!eventId) return;
    setSubmittingCancel(true);
    try {
      await cancelAdminEvent(eventId, { reason: cancelReason });
      toastSuccess('イベントを中止しました');
      closeCancelModal();
      await fetchEventDetails();
      openCancelCompModal();
    } catch (err) {
      toastError(err.message || 'イベントの中止に失敗しました');
    } finally {
      setSubmittingCancel(false);
    }
  };

  // ── データ取得関数（フックではない）───────────────────────────────────
  const fetchEventDetails = async () => {
    try {
      const event = await fetchEventDetailFromApi(eventId);
      if (!event) throw new Error('Network response was not ok');

      event.selected_options = normalizeSelectedOptions(event.selected_options);

      setEventData(event);

      const shouldInherit =
        event.performance_flag === 'additional' && event.use_existing_flyers && event.original_event_uuid;
      if (shouldInherit) {
        const parentName = event.original_event_name || event.original_event_title || event.name || '親イベント';
        const parentPerformance =
          event.original_event_performance ||
          event.original_performance_title ||
          event.original_performance_name ||
          event.performance_title ||
          event.performance_type ||
          '';
        setInheritedAssetSource({
          uuid: event.original_event_uuid,
          name: parentName,
          performance: parentPerformance,
        });
        setParentEventInfo({
          name: parentName,
          performance: parentPerformance,
        });
        // 元イベントのフライヤー/バナー参照
        await fetchOriginalEventAssets(event.original_event_uuid);
      } else {
        setInheritedAssetSource(null);
        setFlyerUrls({
          front: event.flyer_front_url ? toUrl(FLYER_IMAGE_PATH, event.flyer_front_url) : null,
          back: event.flyer_back_url ? toUrl(FLYER_IMAGE_PATH, event.flyer_back_url) : null,
        });
        setBannerUrl(event.banner_image_url ? toUrl(BANNER_IMAGE_PATH, event.banner_image_url) : null);
      }

      // 追加公演の親イベント情報を取得（名称/公演名用）
      if (event.performance_flag === 'additional' && event.original_event_uuid) {
        try {
          const parent = await fetchEventDetailFromApi(event.original_event_uuid);
          if (parent) {
            setParentEventInfo({
              name: parent.name || parent.event_name || '',
              performance:
                parent.performance_title ||
                parent.performance_name ||
                parent.performance_type ||
                '',
            });
          } else {
            setParentEventInfo(null);
          }
        } catch {
          setParentEventInfo(null);
        }
      } else {
        setParentEventInfo(null);
      }

    } catch (err) {
      console.error('Error fetching event details:', err);
      toastError('イベント詳細の取得に失敗しました');
    }
  };

  // 会場リンク/住所解決（ID優先、名前マッチ廃止）
  useEffect(() => {
    let cancelled = false;
    const venueId = eventData?.venue_id ?? eventData?.venue?.id ?? null;
    const fallbackName = eventData?.venue || eventData?.venue_name || '';

    (async () => {
      if (!venueId) {
        setVenueLink(null);
        setVenueResolvedAddress('');
        setVenueResolvedName('');
        return;
      }
      if (isTaskOnlyRole) {
        setVenueLink(null);
        setVenueResolvedName((fallbackName || '').trim());
        setVenueResolvedAddress('');
        return;
      }
      if (!cancelled) {
        setVenueLink(`/admin/workspaces/event-operations/venues/${venueId}`);
      }
      try {
        const venue = await getVenueDetail(String(venueId));
        if (cancelled) return;
        setVenueResolvedName((venue?.name || fallbackName || '').trim());
        setVenueResolvedAddress(buildAddress(venue));
      } catch {
        if (!cancelled) {
          setVenueResolvedName((fallbackName || '').trim());
          setVenueResolvedAddress('');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [eventData?.venue_id, eventData?.venue, eventData?.venue_name, isTaskOnlyRole]);

  const fetchTicketSalesSummary = useCallback(async () => {
    if (!eventId || !canViewTicketSales) {
      setTicketSummary(null);
      return;
    }
    setTicketSummaryLoading(true);
    try {
      const summary = await fetchEventTicketSalesSummaryFromApi(eventId);
      setTicketSummary(summary || null);
    } catch (err) {
      console.error('Error fetching ticket sales summary:', err);
      setTicketSummary(null);
      toastError(err.message || '券売サマリーの取得に失敗しました');
    } finally {
      setTicketSummaryLoading(false);
    }
  }, [canViewTicketSales, eventId, toastError]);

  const fetchEventFinanceSummary = useCallback(async () => {
    if (!eventId || !canViewTicketSales) {
      setEventFinanceSummary(null);
      return;
    }
    setEventFinanceSummaryLoading(true);
    try {
      const booksData = await listAdminEventPlBooks(eventId);
      if (!booksData) {
        setEventFinanceSummary(null);
        return;
      }
      const books = Array.isArray(booksData?.books) ? booksData.books : [];
      const actualBooks = books
        .filter((book) => String(book?.book_type || '').toLowerCase() === 'actual')
        .sort((a, b) => {
          const statusPriority = (status) => {
            const key = String(status || '').toLowerCase();
            if (key === 'draft') return 0;
            if (key === 'locked') return 1;
            return 2;
          };
          const diff = statusPriority(a?.status) - statusPriority(b?.status);
          if (diff !== 0) return diff;
          const aTs = Date.parse(a?.created_at || a?.updated_at || '') || 0;
          const bTs = Date.parse(b?.created_at || b?.updated_at || '') || 0;
          return bTs - aTs;
        });
      const primaryBookId = String(actualBooks[0]?.id || '').trim();
      if (!primaryBookId) {
        setEventFinanceSummary(null);
        return;
      }
      const summaryData = await getAdminPlBookSummary(primaryBookId);
      if (!summaryData) {
        setEventFinanceSummary(null);
        return;
      }
      setEventFinanceSummary(summaryData?.summary || null);
    } catch (err) {
      setEventFinanceSummary(null);
    } finally {
      setEventFinanceSummaryLoading(false);
    }
  }, [canViewTicketSales, eventId]);

  const fetchTimetables = useCallback(async () => {
    if (!eventId || !canViewTimetable) {
      setTimetables([]);
      setActiveTimetableTab(null);
      setRuntimeData({ adjustments: [], memos: [], announcements: [] });
      return;
    }
    setTimetableLoading(true);
    try {
      const list = await listEventTimetablesFromApi(eventId);
      setTimetables(list);
      if (list.length) {
        if (!activeTimetableTab || !list.some((t) => t.id === activeTimetableTab)) {
          setActiveTimetableTab(list[0].id);
        }
      } else {
        setActiveTimetableTab(null);
      }
    } catch (err) {
      console.error('Error fetching timetables:', err);
      setTimetables([]);
      setActiveTimetableTab(null);
      toastError(err.message || 'タイムテーブルの取得に失敗しました');
    } finally {
      setTimetableLoading(false);
    }
  }, [canViewTimetable, eventId, activeTimetableTab, toastError]);

  const fetchStageSeatingSummary = useCallback(async () => {
    if (!eventId || !canViewParticipants) {
      setStageSeatingSummary({ layouts: [], shares: [] });
      return;
    }
    setStageSeatingSummaryLoading(true);
    try {
      const json = await getAdminStageSeatingSharedSummary(eventId);
      setStageSeatingSummary({
        layouts: Array.isArray(json?.layouts) ? json.layouts : [],
        shares: Array.isArray(json?.shares) ? json.shares : [],
      });
    } catch (err) {
      console.error('Error fetching stage seating summary:', err);
      setStageSeatingSummary({ layouts: [], shares: [] });
    } finally {
      setStageSeatingSummaryLoading(false);
    }
  }, [canViewParticipants, eventId]);

  const {
    fetchEventTaskProject,
    openEventTaskDetailModal,
    closeEventTaskDetailModal,
    ensureEventTaskProject,
    handleToggleEventTaskStatus,
    patchEventTaskInline,
    handleSaveEventTaskDescription,
    handleDeleteEventTaskDetail,
    handlePostEventTaskComment,
    handleToggleEventTaskCommentReaction,
    handleCreateEventSubtask,
    handleAddEventTaskDependency,
    handleRemoveEventTaskDependency,
    handleRemoveEventTaskSuccessor,
    handleUploadEventTaskAttachment,
    handleDeleteEventTaskAttachment,
    handleDownloadEventTaskAttachment,
  } = useEventDetailsTaskActions({
    TASK_COMMENT_REACTION_EMOJI_SET,
    applyEventTaskToState,
    canEditEventTasks,
    canViewEventTasks,
    confirmDialog,
    createTaskComment,
    createTaskDependency,
    createTaskItem,
    deleteTaskAttachment,
    deleteTaskDependency,
    deleteTaskItem,
    downloadTaskAttachment,
    ensurePrimaryEventTaskProject,
    eventId,
    eventTaskAttachmentInputRef,
    eventTaskCanEdit,
    eventTaskCommentReactionBusyMap,
    eventTaskCreating,
    eventTaskDependencyLinkType,
    eventTaskDetailCommentBody,
    eventTaskDetailForm,
    eventTaskDetailModal,
    eventTaskDetailTask,
    eventTaskNewSubtaskAssigneeId,
    eventTaskNewSubtaskDueOn,
    eventTaskNewSubtaskTitle,
    eventTaskProject,
    formatTaskDateInput,
    getEventTaskPrimaryAssignee,
    getPrimaryEventTaskProject,
    getTaskProjectDetail,
    isTaskDoneStatus,
    listTaskAttachments,
    listTaskComments,
    listTaskDependencies,
    normalizeEventTaskStatus,
    normalizeTaskPriority,
    removeEventTaskFromState,
    setEventSubtaskInlineEdit,
    setEventTaskAddingDependency,
    setEventTaskAddingDependencyTaskId,
    setEventTaskAllTasks,
    setEventTaskAllTasksLoading,
    setEventTaskAssignableAdmins,
    setEventTaskAttachments,
    setEventTaskAttachmentsLoading,
    setEventTaskCanEdit,
    setEventTaskCommentReactionBusyMap,
    setEventTaskCreating,
    setEventTaskCreatingSubtask,
    setEventTaskDeletingAttachmentId,
    setEventTaskDependencies,
    setEventTaskDependenciesLoading,
    setEventTaskDependencyLinkType,
    setEventTaskDependencyPickerFocused,
    setEventTaskDependencyQuery,
    setEventTaskDetailCommentBody,
    setEventTaskDetailComments,
    setEventTaskDetailCommentsLoading,
    setEventTaskDetailDeleting,
    setEventTaskDetailFeedTab,
    setEventTaskDetailForm,
    setEventTaskDetailModal,
    setEventTaskDetailPostingComment,
    setEventTaskDetailSaving,
    setEventTaskError,
    setEventTaskLoading,
    setEventTaskMetaEditField,
    setEventTaskNewSubtaskAssigneeId,
    setEventTaskNewSubtaskDueOn,
    setEventTaskNewSubtaskInlineEditField,
    setEventTaskNewSubtaskTitle,
    setEventTaskProject,
    setEventTaskRecentTasks,
    setEventTaskRemovingDependencyTaskId,
    setEventTaskSections,
    setEventTaskUpdatingId,
    setEventTaskUploadingAttachment,
    toastError,
    toastSuccess,
    toggleTaskCommentReaction,
    updateTaskItem,
    uploadTaskAttachment,
  });

  const fetchOriginalEventAssets = async (originalEventUuid) => {
    try {
      const event = await fetchEventDetailFromApi(originalEventUuid);
      if (!event) throw new Error('Network response was not ok');
      setInheritedAssetSource({
        uuid: originalEventUuid,
        name: event.name || event.event_name || inheritedAssetSource?.name || '親イベント',
        performance:
          event.performance_title ||
          event.performance_name ||
          event.performance_type ||
          event.sub_title ||
          '',
      });
      setFlyerUrls({
        front: event.flyer_front_url ? toUrl(FLYER_IMAGE_PATH, event.flyer_front_url) : null,
        back: event.flyer_back_url ? toUrl(FLYER_IMAGE_PATH, event.flyer_back_url) : null,
      });
      const origBanner = event.banner_image_url ? toUrl(BANNER_IMAGE_PATH, event.banner_image_url) : null;
      setBannerUrl(origBanner);
    } catch (err) {
      console.error('Error fetching original event flyer:', err);
      toastError(err?.message || 'フライヤー画像の取得に失敗しました');
    }
  };

  const fetchGenres = async () => {
    try {
      const data = await listGenres();
      setGenres(normalizeGenresPayload(data));
    } catch (err) {
      console.error('Error fetching genres:', err);
      toastError(err?.message || 'ジャンル情報の取得に失敗しました');
    }
  };

  const fetchCastingInfo = async () => {
    if (!canViewCastingSettings && !canViewParticipants && !canViewContractStatus) {
      setCastingInfo([]);
      return;
    }
    try {
      const data = await listEventCastingInfo(eventId);
      setCastingInfo(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching casting info:', err);
      setCastingInfo([]);
      toastError('キャスティング情報の取得に失敗しました');
    }
  };

  const fetchCancelCompTargets = useCallback(async () => {
    if (!eventId) return;
    setCancelCompLoading(true);
    try {
      const artists = await listCancelCompensationTargets(eventId);
      setCancelCompTargets(
        artists.map((a) => ({
          ...a,
          // 既に中止保証金額が決定済みの場合は、一括設定対象から除外（上書き防止）
          selection_locked:
            a?.event_cancel_amount !== null &&
            a?.event_cancel_amount !== undefined &&
            String(a.event_cancel_amount).trim() !== '',
          excluded:
            a?.event_cancel_amount !== null &&
            a?.event_cancel_amount !== undefined &&
            String(a.event_cancel_amount).trim() !== '',
        }))
      );
    } catch (err) {
      console.error('fetchCancelCompTargets error:', err);
      toastError(err.message || '中止保証金額候補の取得に失敗しました。');
      setShowCancelCompModal(false);
    } finally {
      setCancelCompLoading(false);
    }
  }, [eventId, toastError]);

  const openCancelCompModal = useCallback(() => {
    if (!canEditEventInfo) {
      toastError(eventReadOnlyTitle);
      return;
    }
    setCancelCompTargets([]);
    setCancelCompLoading(true);
    setCancelCompCalcBusy(true);
    setCancelCompMode('percent');
    setCancelCompPercent('');
    setCancelCompFixed('');
    setShowCancelCompModal(true);
    fetchCancelCompTargets();
  }, [canEditEventInfo, eventReadOnlyTitle, fetchCancelCompTargets, toastError]);

  const closeCancelCompModal = useCallback(() => {
    setShowCancelCompModal(false);
  }, []);

  const fetchAssignmentsForThisEvent = async () => {
    if (!canViewParticipants && !canViewContractStatus) {
      setAssignmentRows([]);
      return;
    }
    try {
      const data = await getAdminEventCastingProgress(eventId);
      setAssignmentRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching casting-progress:', err);
      setAssignmentRows([]);
      toastError('割当情報の取得に失敗しました');
    }
  };

  const searchManualParticipantCandidates = useCallback(
    async (keyword = '') => {
      if (!manualParticipantModalOpen) return;
      setManualParticipantLoading(true);
      try {
        const query = {
          page: 1,
          limit: 100,
          sort: 'login',
          preReg: 'hide',
        };
        const normalizedKeyword = String(keyword || '').trim();
        if (normalizedKeyword) query.name = normalizedKeyword;
        const data = await listAdminArtists(query);
        const rows = Array.isArray(data?.artists) ? data.artists : [];
        const currentArtistIds = new Set(
          (Array.isArray(assignmentRows) ? assignmentRows : [])
            .filter((row) => PARTICIPANT_STATUSES.has(String(row.contract_status || row.status || '').toLowerCase()))
            .map((row) => String(row.artist_id || '').trim())
            .filter(Boolean)
        );
        setManualParticipantCandidates(rows.filter((row) => !currentArtistIds.has(String(row.artist_id || '').trim())));
      } catch (err) {
        setManualParticipantCandidates([]);
        toastError(err.message || 'アーティスト候補の取得に失敗しました');
      } finally {
        setManualParticipantLoading(false);
      }
    },
    [assignmentRows, manualParticipantModalOpen, toastError]
  );

  useEffect(() => {
    if (!manualParticipantModalOpen) return undefined;
    const timer = window.setTimeout(() => {
      searchManualParticipantCandidates(manualParticipantSearch);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [manualParticipantModalOpen, manualParticipantSearch, searchManualParticipantCandidates]);

  const openManualParticipantModal = useCallback(() => {
    setManualParticipantSearch('');
    setManualParticipantCandidates([]);
    setManualParticipantModalOpen(true);
  }, []);

  const closeManualParticipantModal = useCallback(() => {
    setManualParticipantModalOpen(false);
    setManualParticipantSearch('');
    setManualParticipantCandidates([]);
  }, []);

  const refreshParticipantsAfterManualChange = async () => {
    await Promise.all([fetchEventDetails(), fetchAssignmentsForThisEvent(), fetchCastingInfo(), refreshCastingSummary()]);
  };

  const handleAddManualParticipant = async (artist) => {
    const artistId = String(artist?.artist_id || '').trim();
    if (!eventId || !artistId) return;
    setManualParticipantSavingId(artistId);
    try {
      await addEventManualParticipant(eventId, { artistId });
      toastSuccess('契約なし参加アーティストを追加しました');
      await refreshParticipantsAfterManualChange();
      await searchManualParticipantCandidates(manualParticipantSearch);
    } catch (err) {
      toastError(err.message || '契約なし参加アーティストの追加に失敗しました');
    } finally {
      setManualParticipantSavingId('');
    }
  };

  const handleRemoveManualParticipant = async (artist) => {
    const artistId = String(artist?.artist_id || '').trim();
    if (!eventId || !artistId) return;
    const confirmed = await confirmDialog({
      message: `${artist?.name || 'アーティスト'}を契約なし参加から解除しますか？`,
      danger: true,
    });
    if (!confirmed) return;
    setManualParticipantRemovingId(artistId);
    try {
      await removeEventManualParticipant(eventId, artistId);
      toastSuccess('契約なし参加を解除しました');
      await refreshParticipantsAfterManualChange();
    } catch (err) {
      toastError(err.message || '契約なし参加の解除に失敗しました');
    } finally {
      setManualParticipantRemovingId('');
    }
  };

  const {
    buildPreviewRows,
    buildHeaderGroups,
    pickColumnWidths,
    pickRowHeights,
    pickTitleStyle,
    applyRowAdjustments,
    buildPreviewColumns,
  } = useEventDetailsRuntimePreview({
    DEFAULT_TIMETABLE_COLUMNS,
    DEFAULT_TITLE_STYLE,
    activeTimetableTab,
    adjustmentsMap,
    clearHighlightTimer,
    getDefaultColumnAlign,
    highlightTimerRef,
    setNowTimetableRowId,
    timetables,
  });

  const fetchLatestStatusAcrossContracts = async () => {
    if (!canViewContractStatus) {
      setLatestByArtist(new Map());
      setContractStatusRows([]);
      return;
    }
    try {
      const list = await listEventContractArtistStatuses(eventId, { scope: 'all_contracts' });

      const normalizedRows = list.map((r) => ({
        artist_id: r.artist_id || null,
        artist_name: r.artist_name || '',
        profile_picture: r.profile_picture || null,
        gender: r.gender || null,
        status: String(r.status || '').toLowerCase(),
        invoice_resubmission_requested: r.invoice_resubmission_requested === true,
        contract_id: r.contract_id || null,
        contract_artist_id: r.contract_artist_id || null,
        last_ts: r.last_ts || r.lastTs || null,
        event_count: Number(r.event_count) || 0,
        payment_item_label: r.payment_item_label || '出演料',
        main_fee: Number(r.main_fee ?? r.fee ?? 0) || 0,
        additional_rewards: Array.isArray(r.additional_rewards) ? r.additional_rewards : [],
      }));

      const latestMap = new Map();
      for (const row of normalizedRows) {
        if (!row.artist_id) continue;
        const current = latestMap.get(row.artist_id);
        const nextTs = toTimestamp(row.last_ts);
        const currentTs = toTimestamp(current?.lastUpdatedAt);
        const nextParticipating = PARTICIPANT_STATUSES.has(String(row.status || '').toLowerCase());
        const currentParticipating = PARTICIPANT_STATUSES.has(String(current?.status || '').toLowerCase());
        if (
          !current ||
          (nextParticipating && !currentParticipating) ||
          (nextParticipating === currentParticipating && (nextTs ?? -1) >= (currentTs ?? -1))
        ) {
          latestMap.set(row.artist_id, {
            status: row.status,
            contractId: row.contract_id,
            contractArtistId: row.contract_artist_id,
            lastUpdatedAt: row.last_ts,
          });
        }
      }

      setLatestByArtist(latestMap);
      setContractStatusRows(normalizedRows);
    } catch (err) {
      console.error('Error fetching latest artist status (multi):', err);
      setLatestByArtist(new Map());
      setContractStatusRows([]);
      toastError('契約ステータスの取得に失敗しました');
    }
  };

  const fetchGroupEvents = useCallback(async () => {
    if (!eventId) {
      setGroupEvents([]);
      setHasGroupEvents(false);
      setGroupRootEventUuid(null);
      return;
    }
    setGroupEventsLoading(true);
    try {
      const data = await getEventGroupEvents(eventId);
      const hasGroup = data?.has_group === true;
      const list = Array.isArray(data?.events) ? data.events : [];
      setHasGroupEvents(hasGroup && list.length > 0);
      setGroupEvents(hasGroup ? list : []);
      setGroupRootEventUuid(data?.group_root_event_uuid || null);
      setGroupFlyerLandscapeMap({});
    } catch (err) {
      console.error('Error fetching group events:', err);
      setHasGroupEvents(false);
      setGroupEvents([]);
      setGroupRootEventUuid(null);
    } finally {
      setGroupEventsLoading(false);
    }
  }, [eventId]);

  const fetchChatGroupSetting = useCallback(async () => {
    if (!canUseEventChat) {
      setChatGroupSetting({
        has_group: false,
        group_root_event_uuid: null,
        group_event_count: 0,
        chat_mode: CHAT_GROUP_MODE.SEPARATE,
        unified_thread_id: null,
        updated_by_admin_id: null,
        updated_at: null,
      });
      setChatGroupDraftMode(CHAT_GROUP_MODE.SEPARATE);
      setChatGroupSettingLoading(false);
      return;
    }
    if (!eventId) {
      setChatGroupSetting({
        has_group: false,
        group_root_event_uuid: null,
        group_event_count: 0,
        chat_mode: CHAT_GROUP_MODE.SEPARATE,
        unified_thread_id: null,
        updated_by_admin_id: null,
        updated_at: null,
      });
      setChatGroupDraftMode(CHAT_GROUP_MODE.SEPARATE);
      return;
    }
    setChatGroupSettingLoading(true);
    try {
      const data = await getEventChatGroupSetting(eventId);
      const next = {
        has_group: data?.has_group === true,
        group_root_event_uuid: data?.group_root_event_uuid || null,
        group_event_count: Number(data?.group_event_count || 0),
        chat_mode:
          data?.chat_mode === CHAT_GROUP_MODE.GROUP_UNIFIED
            ? CHAT_GROUP_MODE.GROUP_UNIFIED
            : CHAT_GROUP_MODE.SEPARATE,
        unified_thread_id: data?.unified_thread_id || null,
        updated_by_admin_id:
          data?.updated_by_admin_id != null ? Number(data.updated_by_admin_id) : null,
        updated_at: data?.updated_at || null,
      };
      setChatGroupSetting(next);
      setChatGroupDraftMode(next.chat_mode);
    } catch (err) {
      console.error('Error fetching chat group setting:', err);
      setChatGroupSetting({
        has_group: false,
        group_root_event_uuid: null,
        group_event_count: 0,
        chat_mode: CHAT_GROUP_MODE.SEPARATE,
        unified_thread_id: null,
        updated_by_admin_id: null,
        updated_at: null,
      });
      setChatGroupDraftMode(CHAT_GROUP_MODE.SEPARATE);
    } finally {
      setChatGroupSettingLoading(false);
    }
  }, [canUseEventChat, eventId]);

  const fetchNotificationSilence = useCallback(async () => {
    if (!eventId) return;
    setNotificationSilenceLoading(true);
    try {
      const data = await getEventNotificationSilence(eventId);
      const windows = Array.isArray(data.windows) ? data.windows : [];
      setNotificationSilenceWindows(windows);
      setNotificationSilenceDrafts(windows.map(normalizeNotificationSilenceDraft));
    } catch (err) {
      console.error('Error fetching notification silence windows:', err);
      toastError(err?.message || '通知サイレント設定の取得に失敗しました');
    } finally {
      setNotificationSilenceLoading(false);
    }
  }, [eventId, normalizeNotificationSilenceDraft, toastError]);

  const openNotificationSilenceModal = useCallback(() => {
    setNotificationSilenceDrafts(
      notificationSilenceWindows.length
        ? notificationSilenceWindows.map(normalizeNotificationSilenceDraft)
        : [createNotificationSilenceDraft()]
    );
    setNotificationSilenceModalOpen(true);
    if (!notificationSilenceWindows.length) {
      fetchNotificationSilence();
    }
  }, [
    createNotificationSilenceDraft,
    fetchNotificationSilence,
    normalizeNotificationSilenceDraft,
    notificationSilenceWindows,
  ]);

  const updateNotificationSilenceDraft = useCallback((index, field, value) => {
    setNotificationSilenceDrafts((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    );
  }, []);

  const addNotificationSilenceDraft = useCallback(() => {
    setNotificationSilenceDrafts((prev) => [...prev, createNotificationSilenceDraft()]);
  }, [createNotificationSilenceDraft]);

  const removeNotificationSilenceDraft = useCallback((index) => {
    setNotificationSilenceDrafts((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  }, []);

  const saveNotificationSilence = useCallback(async () => {
    if (!canEditEventInfo) {
      toastError(eventReadOnlyTitle);
      return;
    }
    const windows = notificationSilenceDrafts
      .filter((item) => item.enabled || item.silentStartsAt || item.silentEndsAt || item.label)
      .map((item) => ({
        enabled: item.enabled !== false,
        label: item.label || '',
        silentStartsAt: dateTimeLocalToIso(item.silentStartsAt),
        silentEndsAt: dateTimeLocalToIso(item.silentEndsAt),
        soundCheckEnabled: item.soundCheckEnabled === true,
        soundCheckScheduledAt: item.soundCheckEnabled ? dateTimeLocalToIso(item.soundCheckScheduledAt) : null,
      }));
    setNotificationSilenceSaving(true);
    try {
      const data = await updateEventNotificationSilence(eventId, { windows });
      const nextWindows = Array.isArray(data.windows) ? data.windows : [];
      setNotificationSilenceWindows(nextWindows);
      setNotificationSilenceDrafts(nextWindows.map(normalizeNotificationSilenceDraft));
      setNotificationSilenceModalOpen(false);
      toastSuccess('通知サイレント設定を保存しました');
    } catch (err) {
      console.error('Error saving notification silence windows:', err);
      toastError(err?.message || '通知サイレント設定の保存に失敗しました');
    } finally {
      setNotificationSilenceSaving(false);
    }
  }, [
    canEditEventInfo,
    dateTimeLocalToIso,
    eventId,
    eventReadOnlyTitle,
    normalizeNotificationSilenceDraft,
    notificationSilenceDrafts,
    toastError,
    toastSuccess,
  ]);

  const participantSections = useMemo(() => {
    const statusFromRow = (row) => {
      const latest = latestByArtist.get(row.artist_id);
      return String(latest?.status || row.contract_status || row.status || '').toLowerCase();
    };
    const activeRows = (assignmentRows || []).filter((row) => PARTICIPANT_STATUSES.has(statusFromRow(row)));
    const sortedCasting = (Array.isArray(castingInfo) ? castingInfo : [])
      .slice()
      .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
    const sections = sortedCasting.map((ci) => ({
      id: ci.id,
      label: ci.part,
      sortOrder: ci.sort_order ?? 9999,
      artists: [],
      isUnassigned: false,
    }));
    const sectionMap = new Map();
    sections.forEach((sec) => {
      sectionMap.set(sec.id, sec);
      sectionMap.set(String(sec.id), sec);
    });
    const undecided = [];

    const buildRow = (row, partLabel) => {
      const latest = latestByArtist.get(row.artist_id) || {};
      return {
        artist_id: row.artist_id,
        name: row.artist_name || row.name || '',
        name_en: row.artist_name_en || row.name_en || '',
        profile_picture: row.profile_picture || row.artist_profile_picture || row.pic || '',
        gender: row.gender || row.artist_gender || '',
        birth_year: row.birth_year ?? row.artist_birth_year ?? null,
        birth_month: row.birth_month ?? row.artist_birth_month ?? null,
        birth_day: row.birth_day ?? row.artist_birth_day ?? null,
        is_chief: !!row.is_chief,
        is_concertmaster: !!row.is_concertmaster,
        custom_roles: Array.isArray(row.custom_roles)
          ? row.custom_roles
          : Array.isArray(row.customRoles)
          ? row.customRoles
          : [],
        instagram_url: row.instagram_url || '',
        twitter_url: row.twitter_url || '',
        facebook_url: row.facebook_url || '',
        hp_url: row.hp_url || '',
        profile_one_liner: row.profile_one_liner || '',
        partLabel,
        contractTs: toTimestamp(latest.lastUpdatedAt || row.contract_updated_at || row.contractUpdatedAt),
        manual_participant: row.manual_participant === true,
        manual_participant_note: row.manual_participant_note || '',
      };
    };

    activeRows.forEach((row) => {
      const section = sectionMap.get(row.casting_info_id);
      const partLabel = section?.label || 'パート未設定';
      const record = buildRow(row, partLabel);
      if (section) section.artists.push(record);
      else undecided.push(record);
    });

    const sortMembers = (list) => {
      list.sort((a, b) => {
        if (!!a.is_concertmaster !== !!b.is_concertmaster) {
          return a.is_concertmaster ? -1 : 1;
        }
        if (!!a.is_chief !== !!b.is_chief) return a.is_chief ? -1 : 1;
        const aTs = a.contractTs;
        const bTs = b.contractTs;
        if (aTs != null && bTs != null && aTs !== bTs) return aTs - bTs;
        if (aTs != null && bTs == null) return -1;
        if (aTs == null && bTs != null) return 1;
        return (a.name || '').localeCompare(b.name || '', 'ja');
      });
    };

    sections.forEach((sec) => sortMembers(sec.artists));
    sortMembers(undecided);

    const decided = sections.filter((sec) => sec.artists.length > 0);
    if (undecided.length) {
      decided.push({
        id: 'unassigned',
        label: 'パート未設定',
        artists: undecided,
        isUnassigned: true,
      });
    }
    return decided;
  }, [assignmentRows, castingInfo, latestByArtist]);

  const participantBubbleArtists = useMemo(
    () => participantSections.flatMap((sec) => sec.artists),
    [participantSections]
  );

  const participantSummary = useMemo(() => {
    const artists = Array.isArray(participantBubbleArtists) ? participantBubbleArtists : [];
    const genderCounts = { male: 0, female: 0, unknown: 0 };
    const ageBuckets = new Map();
    let ageUnknown = 0;

    artists.forEach((artist) => {
      const genderKey = normalizeGenderBucket(artist.gender || artist.artist_gender);
      genderCounts[genderKey] = (genderCounts[genderKey] || 0) + 1;
      const age = calcAgeFromParts(artist.birth_year, artist.birth_month, artist.birth_day);
      if (!Number.isFinite(age) || age < 0) {
        ageUnknown += 1;
        return;
      }
      const bucketKey = age < 10 ? 'u10' : String(Math.floor(age / 10) * 10);
      ageBuckets.set(bucketKey, (ageBuckets.get(bucketKey) || 0) + 1);
    });

    const genderText = [
      `男性${genderCounts.male}人`,
      `女性${genderCounts.female}人`,
      `未設定${genderCounts.unknown}人`,
    ].join(' / ');

    const ageParts = Array.from(ageBuckets.entries())
      .sort((a, b) => {
        const aKey = a[0] === 'u10' ? 0 : Number(a[0]) || 0;
        const bKey = b[0] === 'u10' ? 0 : Number(b[0]) || 0;
        return aKey - bKey;
      })
      .map(([key, count]) => {
        const label = key === 'u10' ? '10代未満' : `${key}代`;
        return `${label}${count}人`;
      });
    ageParts.push(`未設定${ageUnknown}人`);

    return {
      total: artists.length,
      genderText,
      ageText: ageParts.join(' / '),
    };
  }, [participantBubbleArtists]);

  // 契約状況テーブル用
  const contractTableRows = useMemo(() => {
    const assignmentByArtistId = new Map();
    for (const row of assignmentRows || []) {
      if (!row?.artist_id || assignmentByArtistId.has(row.artist_id)) continue;
      assignmentByArtistId.set(row.artist_id, row);
    }

    const sourceRows = Array.isArray(contractStatusRows) && contractStatusRows.length
      ? contractStatusRows
      : (assignmentRows || []).map((row) => ({
          artist_id: row.artist_id || null,
          artist_name: row.artist_name || '',
          profile_picture: row.profile_picture || row.artist_profile_picture || null,
          gender: row.gender || row.artist_gender || null,
          status: String(row.contract_status || 'unknown').toLowerCase(),
          contract_id: null,
          contract_artist_id: row.contract_artist_id || null,
          last_ts: row.contract_updated_at || null,
          event_count: 0,
          payment_item_label: '出演料',
          main_fee: null,
          additional_rewards: [],
        }));

    const rows = sourceRows.map((entry, index) => {
      const assignment = assignmentByArtistId.get(entry.artist_id) || {};
      const contractArtistId = entry.contract_artist_id || assignment.contract_artist_id || null;
      const contractDetailPath = contractArtistId
        ? `/admin/workspaces/contracts/${contractArtistId}`
        : null;
      const additional = Array.isArray(entry.additional_rewards) ? entry.additional_rewards : [];
      const rewardLines = [];
      const mainAmount = Number(entry.main_fee);
      if (Number.isFinite(mainAmount)) {
        rewardLines.push({
          key: `main-${contractArtistId || index}`,
          label: String(entry.payment_item_label || '出演料').trim() || '出演料',
          amount: Math.max(0, Math.floor(mainAmount)),
        });
      }
      additional.forEach((row, idx) => {
        const amount = Number(row?.amount);
        if (!Number.isFinite(amount)) return;
        rewardLines.push({
          key: `${row?.id || contractArtistId || index}-${idx}`,
          label: String(row?.item_label || '追加報酬').trim() || '追加報酬',
          amount: Math.max(0, Math.floor(amount)),
        });
      });
      return {
        rowKey: contractArtistId || `${entry.artist_id || 'unknown'}-${entry.contract_id || 'none'}-${index}`,
        artist_id: entry.artist_id || assignment.artist_id || null,
        artist_name: entry.artist_name || assignment.artist_name || '—',
        profile_picture: entry.profile_picture || assignment.profile_picture || assignment.artist_profile_picture || null,
        gender: entry.gender || assignment.gender || assignment.artist_gender || null,
        status: String(entry.status || assignment.contract_status || 'unknown').toLowerCase(),
        contract_id: entry.contract_id || null,
        contract_artist_id: contractArtistId,
        contractDetailPath,
        lastUpdatedAt: entry.last_ts || assignment.contract_updated_at || null,
        isMulti: Number(entry.event_count) > 1,
        rewardLines,
      };
    });

    rows.sort((a, b) => {
      const nameCompare = String(a.artist_name || '').localeCompare(String(b.artist_name || ''), 'ja');
      if (nameCompare !== 0) return nameCompare;
      const aTs = toTimestamp(a.lastUpdatedAt);
      const bTs = toTimestamp(b.lastUpdatedAt);
      if (aTs != null && bTs != null && aTs !== bTs) return bTs - aTs;
      if (aTs != null && bTs == null) return -1;
      if (aTs == null && bTs != null) return 1;
      return String(a.contract_artist_id || '').localeCompare(String(b.contract_artist_id || ''));
    });
    return rows;
  }, [assignmentRows, contractStatusRows]);

  const contractStatusSummary = useMemo(() => {
    const totalContracts = contractTableRows.length;
    const uniqueArtists = new Set(
      contractTableRows
        .map((row) => row.artist_id)
        .filter(Boolean)
        .map((id) => String(id))
    ).size;
    const multiContracts = contractTableRows.filter((row) => row.isMulti).length;
    const statusCounts = new Map();
    contractTableRows.forEach((row) => {
      const status = row.invoice_resubmission_requested
        ? 'invoice_resubmission_pending'
        : String(row.status || 'unknown').toLowerCase();
      statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });
    const statusOrder = [
      'agreed',
      'invoice_resubmission_pending',
      'billed',
      'billing_not_required',
      'pending',
      'counterproposal',
      'cancelled',
      'cancelled_by_event_delete',
      'no_response',
      'unknown',
    ];
    const statusItems = statusOrder
      .map((status) => ({
        status,
        count: statusCounts.get(status) || 0,
        label:
          status === 'invoice_resubmission_pending'
            ? labelStatus('billed', { invoice_resubmission_requested: true })
            : labelStatus(status),
      }))
      .filter((item) => item.count > 0);
    return {
      totalContracts,
      uniqueArtists,
      multiContracts,
      statusItems,
    };
  }, [contractTableRows]);

  // プログレス
  const taskProgressStats = useMemo(() => {
    const total = Number(eventTaskInsights?.totalTaskCount || 0);
    const done = Number(eventTaskInsights?.doneTaskCount || 0);
    const percent = Math.max(
      0,
      Math.min(100, Math.round(Number(eventTaskInsights?.completionRate) || 0))
    );
    return { total, done, percent };
  }, [eventTaskInsights]);
  const ticketProgressStats = useMemo(() => {
    const sold = Number(ticketSummary?.sold_total ?? ticketSummary?.soldTotal ?? 0) || 0;
    const capacity = Number(ticketSummary?.total_capacity ?? ticketSummary?.totalCapacity ?? 0) || 0;
    const pctFromSummary = Number(ticketSummary?.progress_pct ?? ticketSummary?.progressPct);
    const rawPct =
      capacity > 0
        ? (sold / capacity) * 100
        : Number.isFinite(pctFromSummary)
          ? pctFromSummary
          : 0;
    const percent = Math.max(0, Math.min(100, Math.round(rawPct || 0)));
    return { sold, capacity, percent };
  }, [ticketSummary]);
  const eventFinanceVisual = useMemo(() => {
    const revenue = Number(eventFinanceSummary?.total?.revenue_incl || 0) || 0;
    const expense = Number(eventFinanceSummary?.total?.expense_incl || 0) || 0;
    const profit = Number(eventFinanceSummary?.total?.profit_incl || 0) || 0;
    const scaleMax = Math.max(revenue, expense, 1);
    const revenueWidth = Math.max(0, Math.min(100, Math.round((revenue / scaleMax) * 100)));
    const expenseWidth = Math.max(0, Math.min(100, Math.round((expense / scaleMax) * 100)));
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    return {
      revenue,
      expense,
      profit,
      revenueWidth,
      expenseWidth,
      margin,
    };
  }, [eventFinanceSummary]);
  const progressOverviewItems = useMemo(
    () => [
      {
        key: 'task',
        tone: 'task',
        icon: 'fa-list-check',
        label: 'タスク',
        percent: taskProgressStats.percent,
        locked: !canViewEventTasks,
        note: taskProgressStats.total > 0 ? `${taskProgressStats.done}/${taskProgressStats.total}` : '0/0',
      },
      {
        key: 'casting',
        tone: 'casting',
        icon: 'fa-user',
        label: 'キャスティング',
        percent: castingProgressPercent,
        locked: !canViewCastingProgress,
        note: castingProgressNote,
      },
      {
        key: 'ticket',
        tone: 'ticket',
        icon: 'fa-ticket',
        label: '券売',
        percent: ticketProgressStats.percent,
        locked: !canViewTicketSales,
        note:
          ticketProgressStats.capacity > 0
            ? `${ticketProgressStats.sold}/${ticketProgressStats.capacity}`
            : '0/0',
      },
    ],
    [
      canViewCastingProgress,
      canViewEventTasks,
      canViewTicketSales,
      castingProgressNote,
      castingProgressPercent,
      taskProgressStats.done,
      taskProgressStats.percent,
      taskProgressStats.total,
      ticketProgressStats.capacity,
      ticketProgressStats.percent,
      ticketProgressStats.sold,
    ]
  );
  const visibleProgressOverviewItems = useMemo(
    () => progressOverviewItems.filter((item) => !item.locked),
    [progressOverviewItems]
  );
  const isProgressComplete =
    visibleProgressOverviewItems.length > 0 &&
    visibleProgressOverviewItems.every((item) => item.percent >= 100);
  const shouldShowProgressOverview =
    canViewEventTasks || canViewCastingProgress || canViewTicketSales;

  const effectiveCancelCompValue = useMemo(() => {
    if (cancelCompMode === 'percent') {
      const p = Number(cancelCompPercent);
      return Number.isFinite(p) && p >= 0 ? p : null;
    }
    const v = Number(cancelCompFixed);
    return Number.isFinite(v) && v >= 0 ? v : null;
  }, [cancelCompMode, cancelCompPercent, cancelCompFixed]);

  // 2秒デバウンスで「確定した入力値」を採用し、行内プレビューの計算タイミングを安定させる
  useEffect(() => {
    if (!showCancelCompModal) {
      if (cancelCompCalcTimerRef.current) {
        clearTimeout(cancelCompCalcTimerRef.current);
        cancelCompCalcTimerRef.current = null;
      }
      setCancelCompCalcBusy(false);
      setCancelCompDebouncedMode(cancelCompMode);
      setCancelCompDebouncedValue(null);
      return;
    }

    if (cancelCompCalcTimerRef.current) {
      clearTimeout(cancelCompCalcTimerRef.current);
    }

    cancelCompCalcTimerRef.current = setTimeout(() => {
      const nextMode = cancelCompMode;
      const nextValue = (() => {
        if (nextMode === 'percent') {
          const p = Number(cancelCompPercent);
          return Number.isFinite(p) && p >= 0 ? p : null;
        }
        const v = Number(cancelCompFixed);
        return Number.isFinite(v) && v >= 0 ? v : null;
      })();

      setCancelCompDebouncedMode(nextMode);
      setCancelCompDebouncedValue(nextValue);

      if (nextValue == null) {
        setCancelCompCalcBusy(false);
        return;
      }

      setCancelCompCalcBusy(true);
      setTimeout(() => setCancelCompCalcBusy(false), 350);
    }, 2000);

    return () => {
      if (cancelCompCalcTimerRef.current) {
        clearTimeout(cancelCompCalcTimerRef.current);
        cancelCompCalcTimerRef.current = null;
      }
    };
  }, [showCancelCompModal, cancelCompMode, cancelCompPercent, cancelCompFixed]);

  const plannedCancelCompByContractArtistId = useMemo(() => {
    const planned = new Map();
    const baseValue = cancelCompDebouncedValue;
    if (baseValue == null) return planned;

    for (const t of cancelCompTargets) {
      if (t.selection_locked) continue;
      if (t.excluded) continue;

      const fee = Number(t.fee || 0);
      let amount = 0;
      if (cancelCompDebouncedMode === 'percent') {
        amount = Math.floor(fee * (baseValue / 100));
      } else {
        amount = Math.floor(baseValue);
      }
      if (amount < 0 || !Number.isFinite(amount)) amount = 0;

      planned.set(t.contract_artist_id, amount);
    }

    return planned;
  }, [cancelCompTargets, cancelCompDebouncedMode, cancelCompDebouncedValue]);

  const handleApplyCancelCompBulk = async () => {
    if (!canEditEventInfo) {
      toastError(eventReadOnlyTitle);
      return;
    }
    if (!eventId) return;
    const value = effectiveCancelCompValue;
    if (value == null) {
      toastError(
        cancelCompMode === 'percent'
          ? '補償割合（％）は0以上の数値で入力してください。'
          : '補償金額（円）は0以上の数値で入力してください。'
      );
      return;
    }
    const targetIds = cancelCompTargets
      .filter((t) => !t.excluded && !t.invoice_confirmed)
      .map((t) => t.contract_artist_id);
    if (targetIds.length === 0) {
      toastError('更新対象となるアーティストが選択されていません。');
      return;
    }

    setCancelCompSaving(true);
    try {
      const payload = {
        mode: cancelCompMode,
        value,
        contractArtistIds: targetIds,
      };
      await bulkSetCancelCompensation(eventId, payload);
      toastSuccess('中止保証金額を一括設定しました。');
      setShowCancelCompModal(false);
      // 再描画用に最新ステータスだけ更新しておく
      fetchCastingInfo();
      refreshCastingSummary();
      fetchAssignmentsForThisEvent();
      fetchLatestStatusAcrossContracts();
    } catch (err) {
      console.error('applyCancelCompBulk error:', err);
      toastError(err.message || '中止保証金額の一括設定に失敗しました。');
    } finally {
      setCancelCompSaving(false);
    }
  };

  // クリックでチャットへ
  const openChat = useCallback(() => {
    if (!canEditEventInfo) {
      toastError(eventReadOnlyTitle);
      return;
    }
    if (!eventId) return;
    markEventAsRead(eventId);
    navigate(`/admin/workspaces/event-operations/event-chat?event=${eventId}&view=chat`);
  }, [canEditEventInfo, eventId, eventReadOnlyTitle, markEventAsRead, navigate, toastError]);

  const openCastingShortcut = useCallback((type) => {
    if (!eventId) return;
    const encodedEventId = encodeURIComponent(eventId);
    const state = {
      eventUuid: eventId,
      eventUuids: [eventId],
      primaryEventUuid: eventId,
      source: 'event-detail-shortcut',
    };
    if (type === 'offer') {
      navigate(`/admin/workspaces/casting/offers/new?event_uuid=${encodedEventId}`, { state });
      return;
    }
    navigate(`/admin/workspaces/contracts/form?event_uuid=${encodedEventId}`, { state });
  }, [eventId, navigate]);

  // 契約詳細へ（行・キーボード対応）
  const goContractDetail = useCallback((path, e) => {
    if (!path) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    navigate(path);
  }, [navigate]);

  // タイトル表示（イベント名＋公演名を分離して表示）
  const performanceTypeJapanese = PERFORMANCE_TYPE_JAPANESE;
  const titleText = useMemo(() => eventData?.name || '', [eventData]);
  const multiPerformanceRows = useMemo(() => (
    Array.isArray(eventData?.performances) ? eventData.performances : []
  ), [eventData]);
  const isMultiPerformanceEvent = eventData?.event_mode === 'multi_performance' && multiPerformanceRows.length > 0;
  useEffect(() => {
    // 初期はどの公演も未選択（＝上部は共通フライヤーを表示）。自動で先頭を選ばない。
    if (!isMultiPerformanceEvent) {
      setSelectedMultiPerformanceId(null);
      return;
    }
    if (!selectedMultiPerformanceId) return;
    // 選択中の公演が一覧から消えた場合のみ選択を解除する。
    const exists = multiPerformanceRows.some(
      (performance) => String(performance?.id || '') === String(selectedMultiPerformanceId)
    );
    if (!exists) {
      setSelectedMultiPerformanceId(null);
    }
  }, [isMultiPerformanceEvent, multiPerformanceRows, selectedMultiPerformanceId]);

  const selectedMultiPerformance = useMemo(
    () => multiPerformanceRows.find((performance) => selectedMultiPerformanceId &&
      String(performance?.id || '') === String(selectedMultiPerformanceId)) || null,
    [multiPerformanceRows, selectedMultiPerformanceId]
  );
  const {
    usesParentAssets,
    flyerSideText,
    openFlyerPreview,
    renderInheritanceNotice,
    promptFlyerUpload,
    handleFlyerInputChange,
    handleFlyerDelete,
    promptPerformanceFlyerUpload,
    handlePerformanceFlyerInputChange,
    handlePerformanceFlyerDelete,
    promptBannerUpload,
    handleBannerInputChange,
    handleBannerDelete,
  } = useEventDetailsAssets({
    BANNER_IMAGE_PATH,
    FLYER_IMAGE_PATH,
    UNSUPPORTED_IMAGE_MESSAGE,
    bannerFileInputRef,
    bannerUrl,
    canEditEventInfo,
    confirmDialog,
    currentFlyerSide,
    deleteAdminEventBanner,
    deleteAdminEventFlyer,
    deleteEventPerformanceFlyer,
    eventData,
    eventId,
    eventReadOnlyTitle,
    flyerFileInputsRef,
    flyerUrls,
    inheritedAssetSource,
    isSupportedImageFile,
    performanceFlyerInputRef,
    selectedMultiPerformance,
    setBannerActionLoading,
    setBannerUrl,
    setCurrentFlyerSide,
    setEventData,
    setFlyerActionLoading,
    setFlyerUrls,
    setPerformanceFlyerLoading,
    setShowFlyerPopup,
    showFlyerPopup,
    toUrl,
    toastError,
    toastSuccess,
    uploadAdminEventBanner,
    uploadAdminEventFlyer,
    uploadEventPerformanceFlyer,
  });
  const groupedStageSeatingSummary = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    const visibleLayouts = Array.isArray(stageSeatingSummary.layouts)
      ? stageSeatingSummary.layouts.slice(0, 6)
      : [];

    visibleLayouts.forEach((layout) => {
      const groupLabel = String(layout?.group_label || '').trim();
      const performanceName = String(layout?.performance_name || '').trim();
      const label = groupLabel || performanceName || 'イベント全体';
      const key = groupLabel ? `group:${groupLabel}` : (performanceName ? `performance:${performanceName}` : 'event-all');
      if (!byKey.has(key)) {
        const group = { key, label, layouts: [] };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).layouts.push(layout);
    });

    return groups;
  }, [stageSeatingSummary.layouts]);
  const getPerformanceVenueInfo = useCallback((performance = {}) => {
    const name = (
      performance.venue_name ||
      performance.venue_name_snapshot ||
      performance.venue ||
      ''
    ).trim();
    const address = buildVenueAddress(performance);
    return {
      venueId: performance.venue_id ?? performance.venueId ?? null,
      name,
      address,
      mapUrl: buildVenueMapUrl({
        name,
        address,
        fallback: performance.prefecture || performance.venue_prefecture || '',
      }),
    };
  }, []);
  const selectedMultiPerformanceVenue = useMemo(
    () => getPerformanceVenueInfo(selectedMultiPerformance || {}),
    [getPerformanceVenueInfo, selectedMultiPerformance]
  );
  const selectedMultiPerformanceFlyerUrl = selectedMultiPerformance?.flyer_front_url
    ? toUrl(FLYER_IMAGE_PATH, selectedMultiPerformance.flyer_front_url)
    : null;
  const selectedMultiPerformancePlaceholderEvent = useMemo(() => {
    if (!selectedMultiPerformance) return eventData;
    return {
      ...eventData,
      eventTitle: eventData?.name || eventData?.event_name || '',
      eventDate: selectedMultiPerformance.performance_date,
      venueName: selectedMultiPerformanceVenue.name,
      performance_type:
        selectedMultiPerformance.performance_name ||
        selectedMultiPerformance.performanceName ||
        '',
      flyer_front_url: selectedMultiPerformance.flyer_front_url || '',
      flyer_placeholder_design_key:
        selectedMultiPerformance.flyer_placeholder_design_key ||
        eventData?.flyer_placeholder_design_key ||
        '',
      flyer_placeholder_color_key:
        selectedMultiPerformance.flyer_placeholder_color_key ||
        eventData?.flyer_placeholder_color_key ||
        '',
    };
  }, [eventData, selectedMultiPerformance, selectedMultiPerformanceVenue.name]);
  const subTitleText = useMemo(() => {
    if (isMultiPerformanceEvent) return '';
    const raw = (eventData?.performance_type && String(eventData.performance_type).trim()) || '';
    if (!raw) return '';
    return performanceTypeJapanese[raw] || raw;
  }, [eventData, performanceTypeJapanese, isMultiPerformanceEvent]);
  const nearestMultiPerformance = useMemo(() => {
    if (!isMultiPerformanceEvent) return null;
    const rows = multiPerformanceRows
      .map((performance, index) => {
        const dateKey = toDateKey(performance?.performance_date || performance?.performanceDate);
        if (!dateKey) return null;
        const timeText = String(performance?.start_time || performance?.open_time || '00:00').slice(0, 5);
        const dateTime = new Date(`${dateKey}T${timeText || '00:00'}:00`);
        const day = new Date(`${dateKey}T00:00:00`);
        if (Number.isNaN(dateTime.getTime()) || Number.isNaN(day.getTime())) return null;
        return {
          performance,
          index,
          dayTime: day.getTime(),
          sortTime: dateTime.getTime(),
        };
      })
      .filter(Boolean);
    if (!rows.length) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = rows
      .filter((row) => row.dayTime >= today.getTime())
      .sort((a, b) => a.sortTime - b.sortTime);
    if (upcoming.length) return upcoming[0];
    return [...rows].sort((a, b) => b.sortTime - a.sortTime)[0];
  }, [isMultiPerformanceEvent, multiPerformanceRows]);
  const headerEventDateLabel = (() => {
    if (isMultiPerformanceEvent) {
      const nearest = nearestMultiPerformance;
      const performance = nearest?.performance;
      const formatted = formatDate(performance?.performance_date || performance?.performanceDate);
      if (!formatted || formatted === '未設定') return '';
      const openTime = performance?.open_time ? `${formatTime(performance.open_time)} 開場` : '';
      const startTime = performance?.start_time ? `${formatTime(performance.start_time)} 開演` : '';
      const endTime = performance?.end_time ? `${formatTime(performance.end_time)} 終演` : '';
      const timeText = [openTime, startTime, endTime].filter(Boolean).join(' / ');
      const performanceName =
        performance?.performance_name ||
        performance?.performanceName ||
        `公演${(nearest?.index ?? 0) + 1}`;
      return ['直近開催:', formatted, timeText, performanceName]
        .filter(Boolean)
        .join(' ');
    }
    if (!eventData?.event_date) return '';
    const formatted = formatDate(eventData.event_date);
    if (!formatted || formatted === '未設定') return '';
    return `開催日: ${formatted}`;
  })();
  const compactHeaderDateLabel = (() => {
    if (isMultiPerformanceEvent) {
      const performance = nearestMultiPerformance?.performance || multiPerformanceRows[0];
      const formatted = formatDate(performance?.performance_date || performance?.performanceDate);
      return formatted && formatted !== '未設定' ? formatted : '';
    }
    const formatted = formatDate(eventData?.event_date);
    return formatted && formatted !== '未設定' ? formatted : '';
  })();
  const compactHeaderSubtitleText = useMemo(() => {
    if (subTitleText) return subTitleText;
    if (!isMultiPerformanceEvent) return '';
    const performance = nearestMultiPerformance?.performance || selectedMultiPerformance || multiPerformanceRows[0];
    return String(
      performance?.performance_name ||
      performance?.performanceName ||
      ''
    ).trim();
  }, [
    isMultiPerformanceEvent,
    multiPerformanceRows,
    nearestMultiPerformance,
    selectedMultiPerformance,
    subTitleText,
  ]);
  const handleEventDetailPageScroll = useCallback((scrollEvent) => {
    const nextVisible = Number(scrollEvent?.currentTarget?.scrollTop || 0) > 96;
    setShowCompactEventHeader((current) => (current === nextVisible ? current : nextVisible));
  }, []);

  const formatAdminDisplay = useCallback((admin) => {
    if (!admin) return '未設定';
    return (
      admin.displayName ||
      admin.display_name ||
      admin.fullName ||
      admin.full_name ||
      admin.nickname ||
      admin.username ||
      '未設定'
    );
  }, []);

  const handleFlyerPopupInnerClick = useCallback((e) => {
    e.stopPropagation();
    const target = e.target;
    const isImage = target.closest?.('.expanded-flyer');
    const isToggle = target.closest?.('.flyer-popup-toggle');
    if (!isImage && !isToggle) {
      setShowFlyerPopup(false);
    }
  }, []);

  const assignedAdmins = useMemo(() => {
    if (!Array.isArray(eventData?.assigned_admins)) {
      return [];
    }
    const seen = new Set();
    return eventData.assigned_admins.filter((admin) => {
      if (!admin) return false;
      const rawKey = admin.admin_user_id ?? admin.id ?? admin.username ?? admin.display_name;
      if (rawKey === null || rawKey === undefined || rawKey === '') return true;
      const key = String(rawKey);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [eventData]);

  const assignedAdminNames = useMemo(
    () => assignedAdmins.map((admin) => formatAdminDisplay(admin)),
    [assignedAdmins, formatAdminDisplay]
  );
  const assignedAdminsDisplay = assignedAdminNames.length ? assignedAdminNames.join(' / ') : '未設定';
  const ticketSites = useMemo(
    () => (Array.isArray(eventData?.ticket_sites) ? eventData.ticket_sites : []),
    [eventData]
  );
  const tickets = useMemo(
    () => (Array.isArray(eventData?.tickets) ? eventData.tickets : []),
    [eventData]
  );
  const formatTicketPrice = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? `${num.toLocaleString()} 円` : '—';
  };
  const formatTicketQuantity = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? `${num} 枚` : '—';
  };
  const renderMultilineText = (text, className = '') =>
    text ? (
      <div className={className} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {String(text)}
      </div>
    ) : null;
  const {
    groupEventRows,
    groupRootPerformanceLabel,
    shouldShowGroupEventsPanel,
    membersVisibleToArtists,
    membersVisibilitySaving,
    membersVisibilityButtonLabel,
    membersVisibilityButtonClass,
    membersVisibilityConfirmState,
    closeMembersVisibilityConfirmModal,
    handleMembersVisibilityConfirmSingle,
    handleMembersVisibilityConfirmGroupAll,
    handleMembersVisibilityToggle,
    notificationSilenceSummary,
    isCurrentEventGroupRoot,
    chatGroupCurrentMode,
    chatGroupDraftChanged,
    canEditChatGroupSetting,
    chatGroupUpdatedAtLabel,
    closeChatGroupConfirmModal,
    openChatGroupConfirmModal,
    submitChatGroupModeChange,
    renderGroupEventRow,
    vendors,
    assignmentButtonLabel,
    assignmentButtonDisabled,
    filteredAssignmentCandidates,
    openAssignmentSettings,
    toggleAssignmentDraftId,
    handleAssignmentSettingsSave,
    progressPct,
    soldTotal,
    totalCapacity,
    grossRevenue,
    netRevenue,
    channelShares,
    ticketCapacityProgress,
    recentStats,
    linePoints,
  } = useEventDetailsGroupSales({
    API_URL,
    CHAT_GROUP_EXECUTE_MODE,
    CHAT_GROUP_MODE,
    EventListRow,
    FLYER_IMAGE_PATH,
    appendVariant,
    assignedAdmins,
    assignmentCandidates,
    assignmentDraftIds,
    assignmentSaving,
    assignmentSearch,
    canEditCastingSettings,
    canEditEventInfo,
    canEditParticipants,
    canUseEventChat,
    chatGroupConfirmState,
    chatGroupDraftMode,
    chatGroupSaving,
    chatGroupSetting,
    eventData,
    eventId,
    eventReadOnlyTitle,
    fetchChatGroupSetting,
    fetchEventAssignmentAdminCandidates,
    fetchGroupEvents,
    fetchTicketSalesSummary,
    formatDateTimeJST,
    genres,
    groupEvents,
    groupFlyerLandscapeMap,
    groupRootEventUuid,
    hasGroupEvents,
    isTodayDate,
    loggedInAdminId,
    navigate,
    notificationSilenceLoading,
    notificationSilenceWindows,
    parsePgIntArray,
    previewEventChatGroupSetting,
    refreshEventChatUnread,
    setAssignmentCandidates,
    setAssignmentCandidatesLoading,
    setAssignmentDraftIds,
    setAssignmentSaving,
    setAssignmentSettingsOpen,
    setChatGroupConfirmState,
    setChatGroupPreviewLoading,
    setChatGroupSaving,
    setEventData,
    setGroupEvents,
    setGroupFlyerLandscapeMap,
    ticketSummary,
    tickets,
    toUrl,
    toastError,
    toastSuccess,
    updateEventAssignments,
    updateEventChatGroupSetting,
    useEventMembersVisibilityControl,
  });

  const {
    eventDetailsView,
  } = useEventDetailsView({
    ...eventDetailsPermissions,
    AdminMemberProfileModal,
    ArtistBubbleView,
    CHAT_GROUP_EXECUTE_MODE,
    CHAT_GROUP_MODE,
    CHAT_GROUP_MODE_LABEL,
    DEFAULT_TITLE_STYLE,
    EventFlyerMedia,
    EventImageGallery,
    EventMaterialExplorer,
    EventMembersVisibilityConfirmModal,
    EventVenueDisplay,
    ExplanationButton,
    PersonAvatar,
    TASK_COMMENT_REACTION_OPTIONS,
    TASK_PRIORITY_LABELS,
    TASK_PRIORITY_ORDER,
    TASK_STATUS_LABELS,
    TASK_STATUS_OPTIONS,
    TicketLineChart,
    activeTimetableTab,
    addNotificationSilenceDraft,
    adjustmentsMap,
    applyRowAdjustments,
    assignedAdminsDisplay,
    assignmentButtonDisabled,
    assignmentButtonLabel,
    assignmentCandidatesLoading,
    assignmentDraftIds,
    assignmentSaving,
    assignmentSearch,
    assignmentSettingsOpen,
    bannerActionLoading,
    bannerUrl,
    buildHeaderGroups,
    buildParticipantProfile,
    buildPreviewColumns,
    buildPreviewRows,
    calcDueDays,
    canCancelEvent,
    canEditChatGroupSetting,
    cancelCompCalcBusy,
    cancelCompFixed,
    cancelCompLoading,
    cancelCompMode,
    cancelCompPercent,
    cancelCompSaving,
    cancelCompTargets,
    cancelReason,
    channelShares,
    chatGroupConfirmState,
    chatGroupCurrentMode,
    chatGroupDraftChanged,
    chatGroupDraftMode,
    chatGroupPreviewLoading,
    chatGroupSaving,
    chatGroupSettingLoading,
    chatGroupUpdatedAtLabel,
    closeCancelCompModal,
    closeCancelModal,
    closeChatGroupConfirmModal,
    closeEventTaskDetailModal,
    closeManualParticipantModal,
    closeMembersVisibilityConfirmModal,
    conciergeApplication,
    contractStatusSummary,
    contractTableRows,
    currentFlyerUrl,
    ensureEventTaskProject,
    eventData,
    eventFinanceSummary,
    eventFinanceSummaryLoading,
    eventFinanceVisual,
    eventId,
    eventReadOnlyTitle,
    eventSubtaskInlineEdit,
    eventTaskAddingDependency,
    eventTaskAddingDependencyTaskId,
    eventTaskAllTasksLoading,
    eventTaskAssignableAdmins,
    eventTaskAttachmentBlockRef,
    eventTaskAttachmentInputRef,
    eventTaskAttachments,
    eventTaskAttachmentsLoading,
    eventTaskCanEdit,
    eventTaskCommentReactionBusyMap,
    eventTaskCreating,
    eventTaskCreatingSubtask,
    eventTaskDeletingAttachmentId,
    eventTaskDependencies,
    eventTaskDependenciesLoading,
    eventTaskDependencyBlockRef,
    eventTaskDependencyCandidateTasks,
    eventTaskDependencyLinkType,
    eventTaskDependencyPickerFocused,
    eventTaskDependencyQuery,
    eventTaskDetailAssigneeAvatarUrl,
    eventTaskDetailAssigneeLabel,
    eventTaskDetailCommentBody,
    eventTaskDetailComments,
    eventTaskDetailCommentsLoading,
    eventTaskDetailDeleting,
    eventTaskDetailDueMeta,
    eventTaskDetailFeedTab,
    eventTaskDetailForm,
    eventTaskDetailModal,
    eventTaskDetailParentTask,
    eventTaskDetailPostingComment,
    eventTaskDetailSaving,
    eventTaskDetailSectionName,
    eventTaskDetailSubtasks,
    eventTaskDetailTask,
    eventTaskError,
    eventTaskInsights,
    eventTaskLoading,
    eventTaskMetaEditField,
    eventTaskNewSubtaskAssigneeId,
    eventTaskNewSubtaskAssigneeLabel,
    eventTaskNewSubtaskDueOn,
    eventTaskNewSubtaskInlineEditField,
    eventTaskNewSubtaskTitle,
    eventTaskProject,
    eventTaskRemovingDependencyTaskId,
    eventTaskSectionNameMap,
    eventTaskUpdatingId,
    eventTaskUploadingAttachment,
    filteredAssignmentCandidates,
    flyerActionLoading,
    flyerSideText,
    flyerToggleLabel,
    flyerUrls,
    formatAdminDisplay,
    formatCurrency,
    formatDate,
    formatDateTimeJST,
    formatDueHint,
    formatFileSize,
    formatTicketPrice,
    formatTicketQuantity,
    formatTime,
    formatYenText,
    genres,
    getDefaultColumnAlign,
    getEventTaskAssigneeLabel,
    getEventTaskCommentAuthorAvatarUrl,
    getEventTaskCommentAuthorName,
    getEventTaskPrimaryAssignee,
    getParticipantImageStyle,
    getPerformanceVenueInfo,
    goContractDetail,
    grossRevenue,
    groupEventRows,
    groupEventsLoading,
    groupRootPerformanceLabel,
    groupedStageSeatingSummary,
    handleAddEventTaskDependency,
    handleAddManualParticipant,
    handleApplyCancelCompBulk,
    handleAssignmentSettingsSave,
    handleBannerDelete,
    handleCreateEventSubtask,
    handleDeleteEventTaskAttachment,
    handleDeleteEventTaskDetail,
    handleDownloadEventTaskAttachment,
    handleFlyerDelete,
    handleFlyerPopupInnerClick,
    handleMembersVisibilityConfirmGroupAll,
    handleMembersVisibilityConfirmSingle,
    handleMembersVisibilityToggle,
    handlePerformanceFlyerDelete,
    handlePostEventTaskComment,
    handleRemoveEventTaskDependency,
    handleRemoveEventTaskSuccessor,
    handleRemoveManualParticipant,
    handleSaveEventTaskDescription,
    handleToggleEventTaskCommentReaction,
    handleToggleEventTaskStatus,
    handleUploadEventTaskAttachment,
    hasMultipleFlyers,
    compactHeaderDateLabel,
    compactHeaderSubtitleText,
    headerEventDateLabel,
    hoveredPreviewRowId,
    infoPublishAtLabel,
    isConciergeEvent,
    isCurrentEventGroupRoot,
    isEventCancelled,
    isInfoExplicitlyUnpublished,
    isMultiPerformanceEvent,
    isProgressComplete,
    isTaskDoneStatus,
    isToday,
    jstTodayYmd,
    labelStatus,
    linePoints,
    loggedInAdminId,
    manualParticipantCandidates,
    manualParticipantLoading,
    manualParticipantModalOpen,
    manualParticipantRemovingId,
    manualParticipantSavingId,
    manualParticipantSearch,
    mapEventTaskCommentReactions,
    mapLinkUrl,
    membersVisibilityButtonClass,
    membersVisibilityButtonLabel,
    membersVisibilityConfirmState,
    membersVisibilitySaving,
    membersVisibleToArtists,
    memoMap,
    multiPerformanceRows,
    navigate,
    netRevenue,
    normalizeEventTaskStatus,
    normalizeTaskPriority,
    notificationSilenceDrafts,
    notificationSilenceLoading,
    notificationSilenceModalOpen,
    notificationSilenceSaving,
    notificationSilenceSummary,
    nowTimetableRowId,
    openAssignmentSettings,
    openCancelCompModal,
    openCancelModal,
    openCastingShortcut,
    openChat,
    openChatGroupConfirmModal,
    openEventTaskDetailModal,
    openFlyerPreview,
    openManualParticipantModal,
    openNotificationSilenceModal,
    openTimetableViewer,
    parentEventLabel,
    parentEventLink,
    parsePgIntArray,
    participantBubbleArtists,
    participantProfile,
    participantSections,
    participantSummary,
    participantView,
    patchEventTaskInline,
    performanceFlyerLoading,
    performanceTypeJapanese,
    pickColumnWidths,
    pickRowHeights,
    pickTitleStyle,
    pinnedPreviewRowIds,
    plannedCancelCompByContractArtistId,
    proceedCancelPreview,
    progressOverviewItems,
    progressPct,
    promptBannerUpload,
    promptFlyerUpload,
    promptPerformanceFlyerUpload,
    recentStats,
    removeNotificationSilenceDraft,
    renderGenreBadges,
    renderGroupEventRow,
    renderInheritanceNotice,
    renderMultilineText,
    runtimeData,
    saveNotificationSilence,
    selectedMultiPerformance,
    selectedMultiPerformanceFlyerUrl,
    selectedMultiPerformancePlaceholderEvent,
    setActiveTimetableTab,
    setAssignmentSearch,
    setAssignmentSettingsOpen,
    setCancelCompFixed,
    setCancelCompMode,
    setCancelCompPercent,
    setCancelCompTargets,
    setCancelReason,
    setChatGroupDraftMode,
    setEventSubtaskInlineEdit,
    setEventTaskDependencyLinkType,
    setEventTaskDependencyPickerFocused,
    setEventTaskDependencyQuery,
    setEventTaskDetailCommentBody,
    setEventTaskDetailFeedTab,
    setEventTaskDetailForm,
    setEventTaskMetaEditField,
    setEventTaskNewSubtaskAssigneeId,
    setEventTaskNewSubtaskDueOn,
    setEventTaskNewSubtaskInlineEditField,
    setEventTaskNewSubtaskTitle,
    setHoveredPreviewRowId,
    setManualParticipantSearch,
    setNotificationSilenceModalOpen,
    setParticipantProfile,
    setParticipantView,
    setPinnedPreviewRowIds,
    setSelectedMultiPerformanceId,
    setShowFlyerPopup,
    shouldShowGroupEventsPanel,
    shouldShowProgressOverview,
    showCompactEventHeader,
    showCancelCompModal,
    showCancelConfirm,
    showCancelModal,
    showFlyerPopup,
    soldTotal,
    stageSeatingSummary,
    stageSeatingSummaryLoading,
    subTitleText,
    submitCancel,
    submitChatGroupModeChange,
    submittingCancel,
    ticketCapacityProgress,
    ticketSites,
    ticketSummary,
    ticketSummaryLoading,
    tickets,
    timetableLoading,
    timetables,
    titleText,
    toggleAssignmentDraftId,
    toggleFlyerFace,
    totalCapacity,
    unread,
    updateNotificationSilenceDraft,
    usesParentAssets,
    vendors,
    venueDisplayName,
    venueId,
    venueLink,
    venueResolvedAddress,
  });

  if (!eventData) {
    return (
      <div className="admin-dashboard">
        <Sidebar />
        <div className="event-detail-page">
          <div className="event-detail-container">
            <DetailPageSkeleton variant="event" media="none" columns={1} showToolbar={false} sectionCount={4} />
          </div>
        </div>
      </div>
    );
  }
  // ── 描画 ────────────────────────────────────────────────────────────
  return (
    <div className="admin-dashboard">
      <Sidebar />
      {/* ★ 追加：ページ全体ラッパ（スクロールはこの層に集約） */}
      <div className="event-detail-page event-detail-page--with-section-index" onScroll={handleEventDetailPageScroll}>
        <input
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          ref={(el) => {
            flyerFileInputsRef.current.front = el;
          }}
          onChange={(e) => handleFlyerInputChange('front', e)}
        />
        <input
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          ref={(el) => {
            flyerFileInputsRef.current.back = el;
          }}
          onChange={(e) => handleFlyerInputChange('back', e)}
        />
        <input
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          ref={bannerFileInputRef}
          onChange={handleBannerInputChange}
        />
        <input
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          ref={performanceFlyerInputRef}
          onChange={handlePerformanceFlyerInputChange}
        />
        <EventDetailsPageLayout
          eventId={eventId}
          isToday={isToday}
          canEditTicketSales={canEditTicketSales}
          view={eventDetailsView}
          onTicketSetupSaved={fetchTicketSalesSummary}
        />
      </div>
    </div>
  );
}

// ステータス和訳バッジ用
